import { useState, useEffect, useRef, type ReactNode } from "react";
import { useParams, Link, useLocation } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { emitTourEvent } from "@/hooks/use-guided-tour";
import { useSettingsFormat } from "@/hooks/use-settings-format";
import {
  useGetInvoice,
  useUpdateInvoice,
  useDeleteInvoice,
  useCreateInvoiceLineItem,
  useUpdateInvoiceLineItem,
  useDeleteInvoiceLineItem,
  useUpdateInvoiceLineItemNote,
  useReorderInvoiceLineItems,
  useListInvoiceCosts,
  useCreateInvoiceCost,
  useUpdateInvoiceCost,
  useDeleteInvoiceCost,
  useListInternalCatalog,
  useGetSettings,
  useListProducts,
  useCreateProduct,
  useCreateInternalCatalogItem,
  useDuplicateInvoice,
  useSendInvoiceEmail,
  useSendInvoiceReminder,
  useSendInvoiceReceipt,
  useListCustomers,
  useCreateCustomer,
  getGetInvoiceQueryKey,
  getGetSettingsQueryKey,
  getListInvoicesQueryKey,
  getListProductsQueryKey,
  getListInvoiceCostsQueryKey,
  getListInternalCatalogQueryKey,
  getListCustomersQueryKey,
  useGetConnectStatus,
  useGenerateInvoicePaymentLink,
  useGetMilestones,
  useUpsertMilestones,
  useCompleteMilestone,
  getListMilestonesQueryKey,
  CONNECT_STATUS_QUERY_KEY,
  useListInvoicePayments,
  useRecordInvoicePayment,
  useDeleteInvoicePayment,
  useSendPaymentReceipt,
  useCreateDepositInvoice,
  useMarkDepositReceived,
  getListInvoicePaymentsQueryKey,
  useCreateInvoiceLineItemGroup,
  useUpdateInvoiceLineItemGroup,
  useDeleteInvoiceLineItemGroup,
  useApproveRecurring,
  type InvoiceDetail,
  type InvoiceLineItem,
  type InvoiceLineItemGroup,
  type CompanySettings,
  type Material,
  type MilestoneItem,
  type InvoicePaymentItem,
} from "@workspace/api-client-react";
import { QuickBooksPushButton } from "@/components/quickbooks-push-button";
import { XeroPushButton } from "@/components/xero-push-button";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { InlineEditField } from "@/components/inline-edit-field";
import { InlineEditDate } from "@/components/inline-edit-date";
import { InlineCustomerSelect } from "@/components/inline-customer-select";
import { SavedBadge } from "@/components/estimate-line-items";
import {
  ArrowLeft, Edit, Trash2, Plus, EyeOff,
  Lock, ChevronDown, ChevronUp, Download, Search, Save, Loader2, User, Copy, Eye, DollarSign, Percent, Circle, FileText, Receipt,
  GripVertical, X, RefreshCw, Mail, CheckCircle, CheckCircle2, Clock, AlertTriangle, Check, PlusCircle,
  Zap, Link2, ExternalLink, Info, BellRing, LayoutTemplate, Flag, Group, Target, ArrowRight, ArrowUp, ArrowDown, Layers, CreditCard, Pencil,
} from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { InfoTip } from "@/components/ui/info-tip";
import { FORMULAS } from "@/lib/formulas";
import { PaymentPlanPanel, type PaymentMode } from "@/components/payment-plan-panel";
import { exportInvoicePDF, previewInvoicePDF, invoicePDFBase64, invoicePDFFilename, previewReceiptPDF, receiptPDFFilename, type ReceiptInvoiceData } from "@/lib/pdf";
import { PDFPreviewDialog } from "@/components/pdf-preview-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CreateCustomerModal, type CreateCustomerFormValues } from "@/components/create-customer-modal";
import { EditorDisclaimer } from "@/components/editor-disclaimer";
import { ProGate } from "@/components/pro-gate";
import { ProjectAssignment } from "@/components/project-assignment";
import { useAuth, getTrialInfo } from "@/hooks/use-auth";
import { EditorActionBar } from "@/components/editor-action-bar";
import type { ProFeatureKey } from "@workspace/pricing";
import { PRO_FEATURES } from "@workspace/pricing";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { parseSelectedTaxLines, reconcileTaxLineIds, type TaxLine } from "@workspace/totals";

const GATE_RECURRING: ProFeatureKey = "RECURRING_INVOICES";
const GATE_MILESTONE: ProFeatureKey = "PROGRESS_PAYMENTS";
const GATE_COST_ADD: ProFeatureKey = "COST_CATALOGUE_ADD";

const updateInvoiceSchema = z.object({
  customerId: z.number().optional(),
  invoiceNumber: z.string().optional(),
  title: z.string().min(1),
  description: z.string().optional(),
  termsAndNotes: z.string().nullable().optional(),
  invoiceDate: z.string().optional(),
  dueDate: z.string().optional(),
});

const lineItemSchema = z.object({
  productId: z.coerce.number().nullable().optional(),
  description: z.string().min(1, "Required"),
  quantity: z.coerce.number().min(0.01).max(99999999, "Maximum value is 99,999,999"),
  // Negative prices are allowed so a discount or credit can be added as a line item.
  unitPrice: z.coerce.number().min(-99999999.99, "Minimum value is -99,999,999").max(99999999.99, "Maximum value is 99,999,999"),
});

/** The server's error message from a failed API call, or `fallback`. */
function apiErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { data?: { error?: unknown } } | null)?.data;
  return typeof data?.error === "string" && data.error ? data.error : fallback;
}

const costSchema = z.object({
  catalogItemId: z.coerce.number().nullable().optional(),
  name: z.string().min(1, "Required"),
  quantity: z.coerce.number().min(0.01),
  unit: z.string().optional(),
  unitCost: z.coerce.number().min(0),
  supplier: z.string().optional(),
  notes: z.string().optional(),
});

const getStatusColor = (status: string) => {
  switch (status) {
    case "paid": return "bg-green-100 text-green-800 border-green-300";
    case "partial": return "bg-yellow-100 text-yellow-800 border-yellow-300";
    case "sent": return "bg-blue-100 text-blue-800 border-blue-300";
    case "overdue": return "bg-red-100 text-red-800 border-red-300";
    case "void": return "bg-slate-100 text-slate-600 border-slate-300";
    default: return "bg-slate-100 text-slate-700 border-slate-300";
  }
};

// ─── Invoice Group Header ──────────────────────────────────────────────────────

const GROUP_ACCENT_COLORS = [
  { label: "Blue",   hex: "#3b82f6" },
  { label: "Green",  hex: "#22c55e" },
  { label: "Red",    hex: "#ef4444" },
  { label: "Yellow", hex: "#eab308" },
  { label: "Purple", hex: "#a855f7" },
  { label: "Orange", hex: "#f97316" },
];

function InvoiceGroupHeader({
  group,
  invoiceId,
  items,
  allGroups,
  children,
}: {
  group: InvoiceLineItemGroup;
  invoiceId: number;
  items: any[];
  allGroups: InvoiceLineItemGroup[];
  children: ReactNode;
}) {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(Boolean(group.collapsedByDefault ?? false));
  const [isEditing, setIsEditing] = useState(!group.name || group.name === "New Group");
  const [name, setName] = useState(group.name ?? "");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();
  const updateGroup = useUpdateInvoiceLineItemGroup();
  const deleteGroup = useDeleteInvoiceLineItemGroup();
  const createItem = useCreateInvoiceLineItem();
  const createGroupDup = useCreateInvoiceLineItemGroup();
  const createCostDup = useCreateInvoiceCost();
  const updateNoteDup = useUpdateInvoiceLineItemNote();
  const [isDuplicatingGroup, setIsDuplicatingGroup] = useState(false);

  const sortedAllGroups = [...allGroups].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const myIndex = sortedAllGroups.findIndex(g => g.id === group.id);
  const canMoveUp = myIndex > 0;
  const canMoveDown = myIndex < sortedAllGroups.length - 1;

  const handleMove = (direction: "up" | "down") => {
    const swapIdx = direction === "up" ? myIndex - 1 : myIndex + 1;
    const swapGroup = sortedAllGroups[swapIdx];
    if (!swapGroup) return;
    const mySort = group.sortOrder ?? myIndex;
    const swapSort = swapGroup.sortOrder ?? swapIdx;
    Promise.all([
      updateGroup.mutateAsync({ invoiceId, id: group.id, data: { sortOrder: swapSort } }),
      updateGroup.mutateAsync({ invoiceId, id: swapGroup.id, data: { sortOrder: mySort } }),
    ]).then(() => queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) }))
      .catch(() => toast({ title: "Failed to reorder groups", variant: "destructive" }));
  };

  const handleAddItemToGroup = () => {
    createItem.mutate(
      { invoiceId, data: { description: "New item", quantity: 1, unitPrice: 0, groupId: group.id } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
          setIsCollapsed(false);
        },
        onError: (err) => toast({ title: apiErrorMessage(err, "Failed to add item"), variant: "destructive" }),
      }
    );
  };

  const handleDuplicateGroup = async () => {
    setIsDuplicatingGroup(true);
    try {
      const mySortOrder = group.sortOrder ?? myIndex;
      const nextGroup = sortedAllGroups[myIndex + 1];
      const newSortOrder = nextGroup
        ? (mySortOrder + (nextGroup.sortOrder ?? myIndex + 1)) / 2
        : mySortOrder + 1;
      const newGroup = await new Promise<{ id: number }>((resolve, reject) => {
        createGroupDup.mutate(
          { invoiceId, data: { name: `${group.name || "Untitled Group"} (copy)`, sortOrder: newSortOrder } },
          { onSuccess: resolve, onError: reject }
        );
      });
      for (const originalItem of items) {
        const newItem = await new Promise<{ id: number }>((resolve, reject) => {
          createItem.mutate(
            { invoiceId, data: { description: originalItem.description, quantity: originalItem.quantity, unitPrice: originalItem.unitPrice, productId: originalItem.productId ?? null, groupId: newGroup.id } },
            { onSuccess: resolve, onError: reject }
          );
        });
        const materials = (originalItem.materials ?? []) as Material[];
        for (const mat of materials) {
          await new Promise<void>((resolve, reject) => {
            createCostDup.mutate(
              { invoiceId, lineItemId: newItem.id, data: { name: mat.name, quantity: mat.quantity, unitCost: mat.unitCost, unit: mat.unit ?? null, supplier: mat.supplier ?? null, notes: mat.notes ?? null, type: (mat.type || "materials") as "labour" | "materials" | "fees", catalogItemId: mat.catalogItemId ?? null } },
              { onSuccess: () => resolve(), onError: reject }
            );
          });
        }
        if (originalItem.privateNote) {
          await new Promise<void>((resolve, reject) => {
            updateNoteDup.mutate(
              { invoiceId, lineItemId: newItem.id, data: { privateNote: originalItem.privateNote! } },
              { onSuccess: () => resolve(), onError: reject }
            );
          });
        }
      }
      queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
      toast({ title: "Group duplicated" });
    } catch {
      toast({ title: "Failed to duplicate group", variant: "destructive" });
    } finally {
      setIsDuplicatingGroup(false);
    }
  };

  useEffect(() => {
    setName(group.name ?? "");
  }, [group.id, group.name]);

  useEffect(() => {
    if (!colorPickerOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target as Node)) {
        setColorPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [colorPickerOpen]);

  const subtotal = items.reduce((s: number, li: any) => s + (li.total ?? 0), 0);

  const handleNameSave = () => {
    setIsEditing(false);
    const trimmed = name.trim();
    if (!trimmed) { setName(group.name ?? ""); return; }
    if (trimmed === group.name) return;
    updateGroup.mutate(
      { invoiceId, id: group.id, data: { name: trimmed } },
      {
        onSuccess: () => queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) }),
        onError: () => { setName(group.name ?? ""); toast({ title: "Failed to save group name", variant: "destructive" }); },
      }
    );
  };

  const handleColorChange = (hex: string | null) => {
    setColorPickerOpen(false);
    updateGroup.mutate(
      { invoiceId, id: group.id, data: { accentColor: hex } },
      {
        onSuccess: () => queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) }),
        onError: () => toast({ title: "Failed to update group color", variant: "destructive" }),
      }
    );
  };

  const handleDelete = () => {
    deleteGroup.mutate(
      { invoiceId, id: group.id },
      {
        onSuccess: () => queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) }),
        onError: () => toast({ title: "Failed to delete group", variant: "destructive" }),
      }
    );
  };

  return (
    <div
      className="border-t-2 border-border"
      style={group.accentColor ? { borderLeft: `4px solid ${group.accentColor}` } : undefined}
    >
      <div className="flex items-center gap-2 px-4 py-2.5 bg-muted/40 hover:bg-muted/60 transition-colors">
        <button
          type="button"
          onClick={() => setIsCollapsed(v => !v)}
          className="p-0.5 text-muted-foreground hover:text-foreground shrink-0"
          aria-label={isCollapsed ? "Expand group" : "Collapse group"}
        >
          {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>

        <div ref={colorPickerRef} className="relative shrink-0">
          <button
            type="button"
            onClick={() => setColorPickerOpen(v => !v)}
            className="w-4 h-4 rounded-full border-2 border-border hover:border-primary transition-colors"
            style={{ background: group.accentColor || "#e2e8f0" }}
            aria-label="Set group color"
            title="Set group color"
          />
          {colorPickerOpen && (
            <div className="absolute left-0 top-6 z-50 flex items-center gap-1 p-2 bg-popover border border-border rounded-md shadow-md">
              {GROUP_ACCENT_COLORS.map(color => (
                <button
                  key={color.hex}
                  type="button"
                  onClick={() => handleColorChange(color.hex)}
                  className="w-5 h-5 rounded-full border-2 hover:scale-110 transition-transform"
                  style={{
                    background: color.hex,
                    borderColor: group.accentColor === color.hex ? "#000" : "transparent",
                  }}
                  aria-label={`Set color ${color.label}`}
                  title={color.label}
                />
              ))}
              {group.accentColor && (
                <button
                  type="button"
                  onClick={() => handleColorChange(null)}
                  className="w-5 h-5 rounded-full border-2 border-border bg-background flex items-center justify-center hover:scale-110 transition-transform"
                  aria-label="Remove color"
                  title="Remove color"
                >
                  <X className="w-3 h-3 text-muted-foreground" />
                </button>
              )}
            </div>
          )}
        </div>

        {isEditing ? (
          <input
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            onBlur={handleNameSave}
            onKeyDown={e => {
              if (e.key === "Enter") { e.preventDefault(); handleNameSave(); }
              if (e.key === "Escape") { setName(group.name ?? ""); setIsEditing(false); }
            }}
            placeholder="Group name..."
            className="flex-1 text-sm font-semibold bg-transparent border-b border-primary outline-none py-0.5 min-w-0"
          />
        ) : (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="flex-1 text-sm font-semibold text-left hover:text-primary transition-colors truncate min-w-0"
            title="Click to rename group"
          >
            {group.name || "Untitled Group"}
          </button>
        )}

        <span className="text-xs text-muted-foreground shrink-0">
          {items.length} item{items.length !== 1 ? "s" : ""}
        </span>
        <span className="text-sm font-medium tabular-nums shrink-0">{formatCurrency(subtotal)}</span>
        <div className="flex items-center shrink-0">
          <button
            type="button"
            onClick={() => handleMove("up")}
            disabled={!canMoveUp || updateGroup.isPending}
            className="p-1 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-20"
            aria-label="Move group up"
            title="Move group up"
          >
            <ArrowUp className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => handleMove("down")}
            disabled={!canMoveDown || updateGroup.isPending}
            className="p-1 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-20"
            aria-label="Move group down"
            title="Move group down"
          >
            <ArrowDown className="w-3.5 h-3.5" />
          </button>
        </div>
        <button
          type="button"
          onClick={handleDuplicateGroup}
          disabled={isDuplicatingGroup}
          className="p-1 text-muted-foreground hover:text-primary transition-colors shrink-0 opacity-60 hover:opacity-100 disabled:opacity-40"
          aria-label="Duplicate group"
          title="Duplicate group"
        >
          {isDuplicatingGroup ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
        <button
          type="button"
          onClick={() => setDeleteOpen(true)}
          disabled={deleteGroup.isPending}
          className="p-1 text-muted-foreground hover:text-destructive transition-colors shrink-0 opacity-60 hover:opacity-100"
          aria-label="Delete group"
          title="Delete group (items will be ungrouped)"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {!isCollapsed && children}
      {!isCollapsed && (
        <button
          type="button"
          onClick={handleAddItemToGroup}
          disabled={createItem.isPending}
          className="w-full flex items-center justify-center gap-1.5 py-2 text-xs text-muted-foreground hover:text-primary hover:bg-muted/30 border-t border-dashed border-border/50 transition-colors disabled:opacity-50"
        >
          {createItem.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Add item to group
        </button>
      )}

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Group?</AlertDialogTitle>
            <AlertDialogDescription>
              "{group.name || "Untitled Group"}" will be removed. All items in this group will be moved to the ungrouped list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Group
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Line Item Row (inline editable, matching estimate style) ─────────────────

function LineItemRow({ item, invoiceId, allItems, index, groups }: { item: InvoiceLineItem; invoiceId: number; allItems: InvoiceLineItem[]; index: number; groups?: InvoiceLineItemGroup[] }) {
  const [description, setDescription] = useState(item.description || "");
  const [quantity, setQuantity] = useState(String(item.quantity));
  const [unitPrice, setUnitPrice] = useState(String(item.unitPrice));
  const [localProductId, setLocalProductId] = useState<number | null>(item.productId ?? null);
  const [localProductName, setLocalProductName] = useState<string | null>(item.productName ?? null);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">("idle");
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { formatCurrency } = useSettingsFormat();
  const [isDuplicating, setIsDuplicating] = useState(false);

  useEffect(() => {
    setDescription(item.description || "");
    setQuantity(String(item.quantity));
    setUnitPrice(String(item.unitPrice));
    setLocalProductId(item.productId ?? null);
    setLocalProductName(item.productName ?? null);
  }, [item.id, item.description, item.quantity, item.unitPrice, item.productId, item.productName]);

  const updateItem = useUpdateInvoiceLineItem();
  const deleteItem = useDeleteInvoiceLineItem();
  const reorderLineItems = useReorderInvoiceLineItems();
  const createItemDup = useCreateInvoiceLineItem();
  const createCostDup = useCreateInvoiceCost();
  const updateNoteDup = useUpdateInvoiceLineItemNote();

  const { data: products } = useListProducts(
    productSearch ? { search: productSearch } : {},
    { query: { queryKey: getListProductsQueryKey(productSearch ? { search: productSearch } : {}), enabled: productSearchOpen } }
  );

  const saveField = (overrides?: { description?: string; quantity?: string; unitPrice?: string }) => {
    const desc = overrides?.description ?? description;
    const qty = parseFloat(overrides?.quantity ?? quantity);
    const price = parseFloat(overrides?.unitPrice ?? unitPrice);
    if (!desc.trim()) { toast({ title: "Description is required", variant: "destructive" }); return; }
    if (isNaN(qty) || qty <= 0) { toast({ title: "Quantity must be greater than 0", variant: "destructive" }); return; }
    if (qty > 99999999) { toast({ title: "Maximum value is 99,999,999", variant: "destructive" }); return; }
    if (isNaN(price)) { toast({ title: "Unit price is required", variant: "destructive" }); return; }
    if (Math.abs(price) > 99999999.99) { toast({ title: "Maximum value is 99,999,999", variant: "destructive" }); return; }
    setSaveStatus("saving");
    updateItem.mutate(
      { invoiceId, id: item.id, data: { description: desc, quantity: qty, unitPrice: price, productId: localProductId, sortOrder: item.sortOrder } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
          setSaveStatus("saved");
          setTimeout(() => setSaveStatus("idle"), 1500);
        },
        onError: (err) => {
          setSaveStatus("idle");
          // Put the fields back to the saved values so the screen matches the invoice.
          setQuantity(String(item.quantity));
          setUnitPrice(String(item.unitPrice));
          toast({ title: apiErrorMessage(err, "Failed to save"), variant: "destructive" });
        }
      }
    );
  };

  const applyProduct = (product: { id: number; name: string; description?: string | null; unitPrice: number }) => {
    const newDesc = description || product.description || product.name;
    const newPrice = String(product.unitPrice);
    setDescription(newDesc);
    setUnitPrice(newPrice);
    setLocalProductId(product.id);
    setLocalProductName(product.name);
    setProductSearchOpen(false);
    setProductSearch("");
    setSaveStatus("saving");
    updateItem.mutate(
      { invoiceId, id: item.id, data: { description: newDesc, quantity: parseFloat(quantity) || 1, unitPrice: product.unitPrice, productId: product.id, sortOrder: item.sortOrder } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
          queryClient.invalidateQueries({ queryKey: getListInvoiceCostsQueryKey(invoiceId, item.id) });
          setSaveStatus("saved");
          setTimeout(() => setSaveStatus("idle"), 1500);
        },
        onError: () => {
          setSaveStatus("idle");
          toast({ title: "Failed to save", variant: "destructive" });
        }
      }
    );
  };

  const onDelete = () => {
    deleteItem.mutate({ invoiceId, id: item.id }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
        toast({ title: "Item removed" });
      },
      onError: (err) => toast({ title: apiErrorMessage(err, "Failed to remove item"), variant: "destructive" }),
    });
  };

  const handleDuplicate = async () => {
    setIsDuplicating(true);
    try {
      const newItem = await new Promise<{ id: number }>((resolve, reject) => {
        createItemDup.mutate(
          { invoiceId, data: { description: item.description, quantity: item.quantity, unitPrice: item.unitPrice, productId: item.productId ?? null, groupId: item.groupId ?? null } },
          { onSuccess: resolve, onError: reject }
        );
      });
      const materials = (item as InvoiceLineItem & { materials?: Material[] }).materials ?? [];
      for (const mat of materials) {
        await new Promise<void>((resolve, reject) => {
          createCostDup.mutate(
            { invoiceId, lineItemId: newItem.id, data: { name: mat.name, quantity: mat.quantity, unitCost: mat.unitCost, unit: mat.unit ?? null, supplier: mat.supplier ?? null, notes: mat.notes ?? null, type: (mat.type || "materials") as "labour" | "materials" | "fees", catalogItemId: mat.catalogItemId ?? null } },
            { onSuccess: () => resolve(), onError: reject }
          );
        });
      }
      if (item.privateNote) {
        await new Promise<void>((resolve, reject) => {
          updateNoteDup.mutate(
            { invoiceId, lineItemId: newItem.id, data: { privateNote: item.privateNote! } },
            { onSuccess: () => resolve(), onError: reject }
          );
        });
      }
      queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
      toast({ title: "Item duplicated" });
    } catch {
      toast({ title: "Failed to duplicate item", variant: "destructive" });
    } finally {
      setIsDuplicating(false);
    }
  };

  const [isGroupMoving, setIsGroupMoving] = useState(false);
  const [groupMenuOpen, setGroupMenuOpen] = useState(false);

  const moveToGroup = (targetGroupId: number | null) => {
    setGroupMenuOpen(false);
    if (targetGroupId === (item.groupId ?? null)) return;
    setIsGroupMoving(true);
    updateItem.mutate(
      { invoiceId, id: item.id, data: { description, quantity: parseFloat(quantity) || 1, unitPrice: parseFloat(unitPrice) || 0, productId: localProductId, sortOrder: item.sortOrder, groupId: targetGroupId } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
          setIsGroupMoving(false);
        },
        onError: () => {
          setIsGroupMoving(false);
          toast({ title: "Failed to move item", variant: "destructive" });
        }
      }
    );
  };

  const [isReordering, setIsReordering] = useState(false);

  const reorderItem = (direction: "up" | "down") => {
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= allItems.length || isReordering) return;
    setIsReordering(true);
    const newOrder = allItems.map((i: any) => i.id);
    newOrder.splice(index, 1);
    newOrder.splice(swapIndex, 0, item.id);
    reorderLineItems.mutate(
      { invoiceId, ids: newOrder },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
          setIsReordering(false);
        },
        onError: () => {
          queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
          setIsReordering(false);
          toast({ title: "Failed to reorder item", variant: "destructive" });
        }
      }
    );
  };

  const localTotal = (parseFloat(quantity) || 0) * (parseFloat(unitPrice) || 0);

  return (
    <div>
      <div className="px-4 py-3 flex gap-3 hover:bg-muted/20 transition-colors group">
        <div className="mt-2 flex flex-col gap-0.5 shrink-0">
          <button
            type="button"
            onClick={() => reorderItem("up")}
            disabled={index === 0 || isReordering}
            className="p-1 rounded text-muted-foreground/60 hover:text-foreground hover:bg-muted transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
            aria-label="Move item up"
          >
            <ChevronUp className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => reorderItem("down")}
            disabled={index === allItems.length - 1 || isReordering}
            className="p-1 rounded text-muted-foreground/60 hover:text-foreground hover:bg-muted transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
            aria-label="Move item down"
          >
            <ChevronDown className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          {/* Product chip / link-product search */}
          <div className="flex items-center gap-2 flex-wrap">
            {localProductName ? (
              <span className="flex items-center justify-between w-full text-base px-3 py-2 rounded-md bg-[hsl(var(--sidebar))] text-[hsl(var(--primary))] font-semibold">
                {localProductName}
                {/* Save indicator sits inside the chip so it never wraps onto its own line and pushes the description down */}
                <span className="flex items-center gap-2 ml-2 shrink-0">
                  {saveStatus === "saving" && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
                  {saveStatus === "saved" && <SavedBadge />}
                  <button
                    type="button"
                    className="text-[hsl(var(--primary))] hover:opacity-70 transition-opacity"
                    onClick={() => { setProductSearchOpen(v => !v); setProductSearch(""); }}
                    title="Change product"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </span>
              </span>
            ) : (
              <>
                <button
                  type="button"
                  className="text-base text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
                  onClick={() => { setProductSearchOpen(v => !v); setProductSearch(""); }}
                >
                  <Search className="w-5 h-5" />
                  Link product
                </button>
                {saveStatus === "saving" && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
                {saveStatus === "saved" && <SavedBadge />}
              </>
            )}
          </div>

          {/* Inline product search popover */}
          {productSearchOpen && (
            <div className="border rounded-md shadow-md bg-card p-2 space-y-1 z-10 relative">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  autoFocus
                  placeholder="Search catalog..."
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                  className="pl-8 text-sm h-8"
                />
              </div>
              {productSearch && products !== undefined && (
                <div className="max-h-40 overflow-y-auto border rounded bg-background">
                  {products.map(p => (
                    <button key={p.id} type="button"
                      className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-muted/60 transition-colors border-b last:border-0"
                      onClick={() => applyProduct(p)}>
                      <div>
                        <p className="text-sm font-medium">{p.name}</p>
                        {p.description && <p className="text-xs text-muted-foreground truncate max-w-[200px]">{p.description}</p>}
                      </div>
                      <span className="text-sm font-semibold text-primary shrink-0 ml-2">{formatCurrency(Number(p.unitPrice))}</span>
                    </button>
                  ))}
                  {products.length === 0 && (
                    <p className="text-xs text-muted-foreground px-3 py-2">No products found</p>
                  )}
                </div>
              )}
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => { setProductSearchOpen(false); setProductSearch(""); }}
              >
                Cancel
              </button>
            </div>
          )}

          {/* Two-column layout: description left, qty/price right */}
          <div className="flex gap-3 items-stretch">
            <Textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              onBlur={() => saveField({ description })}
              placeholder="Describe this line item..."
              className="flex-1 min-h-[72px] text-sm resize-y"
            />

            <div className="flex flex-col items-end justify-between gap-2 shrink-0">
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-1">
                  <label className="text-sm text-muted-foreground shrink-0 w-8 text-right">Qty</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={quantity}
                    onChange={e => setQuantity(e.target.value)}
                    onBlur={() => saveField({ quantity })}
                    className="w-24 h-7 text-sm px-2"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <label className="text-sm text-muted-foreground shrink-0 w-8 text-right">$</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={unitPrice}
                    onChange={e => setUnitPrice(e.target.value)}
                    onBlur={() => saveField({ unitPrice })}
                    className="w-24 h-7 text-sm px-2"
                  />
                </div>
              </div>

              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-1.5">
                  {groups && groups.length > 0 && (
                    <DropdownMenu open={groupMenuOpen} onOpenChange={setGroupMenuOpen}>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-9 w-9 text-muted-foreground hover:text-foreground"
                          disabled={isGroupMoving}
                          title="Move to group"
                        >
                          {isGroupMoving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Group className="w-4 h-4" />}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {item.groupId && (
                          <>
                            <DropdownMenuItem onClick={() => moveToGroup(null)}>
                              Remove from group
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                          </>
                        )}
                        {(groups as InvoiceLineItemGroup[]).filter((g) => g.id !== item.groupId).map((g) => (
                          <DropdownMenuItem key={g.id} onClick={() => moveToGroup(g.id)}>
                            Move to: {g.name}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <Button size="icon" variant="ghost" className="h-9 w-9 text-muted-foreground hover:text-foreground" onClick={handleDuplicate} disabled={isDuplicating} aria-label="Duplicate item">
                        {isDuplicating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Duplicate item</TooltipContent>
                  </Tooltip>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <Button size="icon" variant="ghost" className="h-9 w-9 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setDeleteOpen(true)} aria-label="Delete item">
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Delete item</TooltipContent>
                  </Tooltip>
                </div>
                <span className="font-semibold text-sm">{formatCurrency(localTotal)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Line Item?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete this line item and its internal costs.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground" onClick={onDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Per-Item Workshop (inline, matching estimate style) ──────────────────────

function PerItemWorkshop({ invoiceId, lineItem }: { invoiceId: number; lineItem: InvoiceLineItem }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();

  const [note, setNote] = useState(lineItem.privateNote || "");
  const [noteDirty, setNoteDirty] = useState(false);
  const [costSearch, setCostSearch] = useState("");
  const [costDropdownOpen, setCostDropdownOpen] = useState(false);

  const updateNote = useUpdateInvoiceLineItemNote();
  const deleteCost = useDeleteInvoiceCost();
  const createCost = useCreateInvoiceCost();

  const { data: costs, isLoading: costsLoading } = useListInvoiceCosts(invoiceId, lineItem.id, {
    query: { queryKey: getListInvoiceCostsQueryKey(invoiceId, lineItem.id) }
  });

  const { data: catalog } = useListInternalCatalog({
    query: { queryKey: getListInternalCatalogQueryKey(), enabled: costDropdownOpen }
  });

  const filteredCatalog = catalog?.filter(item =>
    !costSearch || item.name.toLowerCase().includes(costSearch.toLowerCase())
  ) ?? [];

  const saveNote = () => {
    updateNote.mutate(
      { invoiceId, lineItemId: lineItem.id, data: { privateNote: note || null } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
          setNoteDirty(false);
          toast({ title: "Note saved" });
        }
      }
    );
  };

  const onDeleteCost = (costId: number) => {
    deleteCost.mutate({ invoiceId, lineItemId: lineItem.id, id: costId }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListInvoiceCostsQueryKey(invoiceId, lineItem.id) });
        queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
      }
    });
  };

  const addCostFromCatalog = (item: { id: number; name: string; unitCost: number; unit?: string | null; type?: string }) => {
    createCost.mutate(
      { invoiceId, lineItemId: lineItem.id, data: { catalogItemId: item.id, name: item.name, quantity: 1, unitCost: Number(item.unitCost), unit: item.unit || null, supplier: null, notes: null, type: (item.type || "materials") as "labour" | "materials" | "fees" } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListInvoiceCostsQueryKey(invoiceId, lineItem.id) });
          queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
          setCostSearch("");
          setCostDropdownOpen(false);
        },
        onError: () => toast({ title: "Failed to add cost", variant: "destructive" })
      }
    );
  };

  const [createCostModalOpen, setCreateCostModalOpen] = useState(false);
  const [createCostModalName, setCreateCostModalName] = useState("");

  const openCreateCostModal = (name: string) => {
    setCreateCostModalName(name);
    setCostSearch("");
    setCostDropdownOpen(false);
    setCreateCostModalOpen(true);
  };

  const costTotal = costs?.reduce((s, c) => s + c.totalCost, 0) ?? 0;

  return (
    <>
      {createCostModalOpen && (
        <CreateInvoiceCostModal
          invoiceId={invoiceId}
          lineItemId={lineItem.id}
          initialName={createCostModalName}
          onClose={() => setCreateCostModalOpen(false)}
        />
      )}
    <div className="border-l-4 border-slate-400 bg-slate-50 dark:bg-slate-900/40 px-4 py-4 space-y-5 mx-4 mb-3 rounded-r-md shadow-sm">
      {/* Private Note */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Lock className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs font-semibold text-foreground uppercase tracking-wider">Private Note</span>
        </div>
        <Textarea
          value={note}
          onChange={e => { setNote(e.target.value); setNoteDirty(true); }}
          placeholder="Internal reminders, gotchas, schedule notes..."
          className="min-h-[60px] text-sm bg-background"
        />
        {noteDirty && (
          <Button
            size="sm"
            className="mt-2"
            onClick={saveNote}
            disabled={updateNote.isPending}
          >
            {updateNote.isPending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            Save Note
          </Button>
        )}
      </div>

      {/* Internal Costs */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <EyeOff className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs font-semibold text-foreground uppercase tracking-wider">Internal Costs</span>
        </div>

        {costsLoading && <div className="text-xs text-muted-foreground py-2">Loading...</div>}

        {!costsLoading && costs?.length === 0 && !costDropdownOpen && (
          <div className="text-xs text-foreground/70 py-3 text-center border border-dashed border-border rounded-md bg-background/50">
            No internal costs tracked for this item.
          </div>
        )}

        {costs && costs.length > 0 && (
          <div className="border rounded-md divide-y bg-background mb-2">
            {costs.map(cost => (
              <InvoiceCostRow
                key={cost.id}
                cost={cost}
                invoiceId={invoiceId}
                lineItemId={lineItem.id}
                onDelete={() => onDeleteCost(cost.id)}
              />
            ))}
            <div className="flex justify-between items-center px-3 py-2 text-xs font-semibold bg-muted/30">
              <span>Total Internal Cost</span>
              <span>{formatCurrency(costTotal)}</span>
            </div>
          </div>
        )}

        {/* Add Cost search input */}
        <div className="relative">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="Add cost — search catalog or type a name..."
              value={costSearch}
              onChange={e => { setCostSearch(e.target.value); setCostDropdownOpen(true); }}
              onFocus={() => setCostDropdownOpen(true)}
              className="pl-8 h-7 text-xs bg-background"
              disabled={createCost.isPending}
            />
            {createCost.isPending && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 animate-spin text-muted-foreground" />}
          </div>
          {costDropdownOpen && (
            <div className="absolute z-20 left-0 right-0 top-full mt-1 border rounded-md shadow-lg bg-card overflow-hidden max-h-48 overflow-y-auto">
              {filteredCatalog.map(item => (
                <button
                  key={item.id}
                  type="button"
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-muted/60 transition-colors border-b last:border-0 text-xs"
                  onMouseDown={e => { e.preventDefault(); addCostFromCatalog(item); }}
                >
                  <span className="font-medium">{item.name}</span>
                  <span className="text-primary font-semibold ml-2">{formatCurrency(Number(item.unitCost))}/{item.unit || "ea"}</span>
                </button>
              ))}
              {costSearch.trim() && !filteredCatalog.some(c => c.name.toLowerCase() === costSearch.trim().toLowerCase()) && (
                <ProGate feature={GATE_COST_ADD} mode="overlay">
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/60 transition-colors text-xs text-primary font-medium border-t"
                    onMouseDown={e => { e.preventDefault(); openCreateCostModal(costSearch.trim()); }}
                  >
                    <Plus className="w-3 h-3 shrink-0" />
                    Create new cost: "{costSearch.trim()}"
                  </button>
                </ProGate>
              )}
              {!costSearch && filteredCatalog.length === 0 && (
                <p className="text-xs text-muted-foreground px-3 py-2">No catalog items. Type a name to create a custom cost.</p>
              )}
              <button
                type="button"
                className="w-full text-left px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground border-t bg-muted/20"
                onMouseDown={e => { e.preventDefault(); setCostDropdownOpen(false); setCostSearch(""); }}
              >
                Close
              </button>
            </div>
          )}
        </div>
        {costDropdownOpen && (
          <div className="fixed inset-0 z-10" onClick={() => { setCostDropdownOpen(false); setCostSearch(""); }} />
        )}
      </div>
    </div>
    </>
  );
}

// ─── Create New Invoice Cost Modal ────────────────────────────────────────────

const createInvoiceCostModalSchema = z.object({
  name: z.string().min(1, "Required"),
  type: z.enum(["labour", "materials", "fees"]).default("materials"),
  quantity: z.coerce.number().min(0.01, "Must be > 0"),
  unitCost: z.coerce.number().min(0, "Must be ≥ 0"),
  unit: z.string().optional(),
  supplier: z.string().optional(),
  notes: z.string().optional(),
  saveToCatalog: z.boolean().optional(),
});

function CreateInvoiceCostModal({ invoiceId, lineItemId, initialName, onClose }: {
  invoiceId: number;
  lineItemId: number;
  initialName: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();
  const createCost = useCreateInvoiceCost();
  const createCatalogItem = useCreateInternalCatalogItem();
  const [linkedCatalogId, setLinkedCatalogId] = useState<number | null>(null);
  const [linkedCatalogName, setLinkedCatalogName] = useState<string | null>(null);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogSearchOpen, setCatalogSearchOpen] = useState(false);

  const { data: allCatalogItems } = useListInternalCatalog({
    query: { queryKey: getListInternalCatalogQueryKey(), enabled: catalogSearchOpen },
  });
  const catalogItems = catalogSearch
    ? allCatalogItems?.filter(i => i.name.toLowerCase().includes(catalogSearch.toLowerCase()))
    : allCatalogItems;

  const form = useForm<z.infer<typeof createInvoiceCostModalSchema>>({
    resolver: zodResolver(createInvoiceCostModalSchema),
    defaultValues: { name: initialName, type: "materials", quantity: 1, unitCost: 0, unit: "", supplier: "", notes: "", saveToCatalog: false },
  });

  useEffect(() => {
    form.reset({ name: initialName, type: "materials", quantity: 1, unitCost: 0, unit: "", supplier: "", notes: "", saveToCatalog: false });
    setLinkedCatalogId(null);
    setLinkedCatalogName(null);
    setCatalogSearch("");
  }, [initialName]);

  const applyExistingCatalogItem = (item: { id: number; name: string; unitCost: number; unit?: string | null; type?: string }) => {
    setLinkedCatalogId(item.id);
    setLinkedCatalogName(item.name);
    form.setValue("name", item.name);
    form.setValue("unitCost", item.unitCost);
    form.setValue("unit", item.unit || "");
    if (item.type) form.setValue("type", item.type as "labour" | "materials" | "fees");
    setCatalogSearchOpen(false);
    setCatalogSearch("");
  };

  const onSubmit = async (data: z.infer<typeof createInvoiceCostModalSchema>) => {
    let catalogItemId: number | null = linkedCatalogId;
    if (!catalogItemId && data.saveToCatalog) {
      try {
        const newCatalogItem = await new Promise<{ id: number }>((resolve, reject) => {
          createCatalogItem.mutate(
            { data: { name: data.name, description: data.notes || null, unitCost: data.unitCost, unit: data.unit || null, type: data.type } },
            { onSuccess: resolve, onError: reject }
          );
        });
        catalogItemId = newCatalogItem.id;
        queryClient.invalidateQueries({ queryKey: getListInternalCatalogQueryKey() });
      } catch {
        toast({ title: "Failed to save to catalog", variant: "destructive" });
        return;
      }
    }

    createCost.mutate(
      { invoiceId, lineItemId, data: { catalogItemId, name: data.name, quantity: data.quantity, unitCost: data.unitCost, unit: data.unit || null, supplier: data.supplier || null, notes: data.notes || null, type: data.type || "materials" } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListInvoiceCostsQueryKey(invoiceId, lineItemId) });
          queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
          toast({ title: "Cost added" });
          onClose();
        },
        onError: () => toast({ title: "Failed to add cost", variant: "destructive" })
      }
    );
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <DialogHeader>
              <DialogTitle>New Internal Cost</DialogTitle>
              <DialogDescription>Track this cost privately. Never shown to the customer.</DialogDescription>
            </DialogHeader>

            {/* Optional: link to existing catalog item */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Link to catalog item <span className="text-muted-foreground font-normal">(optional)</span></label>
              {linkedCatalogName ? (
                <div className="flex items-center justify-between p-2.5 rounded-md border border-primary/30 bg-primary/5">
                  <p className="text-sm font-medium">{linkedCatalogName}</p>
                  <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setLinkedCatalogId(null); setLinkedCatalogName(null); }}>Unlink</Button>
                </div>
              ) : (
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Search catalog to link..."
                      value={catalogSearch}
                      onChange={e => { setCatalogSearch(e.target.value); setCatalogSearchOpen(true); }}
                      onFocus={() => setCatalogSearchOpen(true)}
                      className="pl-8 h-8 text-sm"
                    />
                  </div>
                  {catalogSearchOpen && catalogSearch && (
                    <div className="absolute z-20 left-0 right-0 top-full mt-0.5 border rounded-md shadow-lg bg-card overflow-hidden max-h-40 overflow-y-auto">
                      {catalogItems?.map(item => (
                        <button key={item.id} type="button"
                          className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-muted/60 transition-colors border-b last:border-0"
                          onMouseDown={e => { e.preventDefault(); applyExistingCatalogItem(item); }}
                        >
                          <p className="text-sm font-medium">{item.name}</p>
                          <span className="text-sm font-semibold text-primary ml-2">{formatCurrency(Number(item.unitCost))}{item.unit ? `/${item.unit}` : ""}</span>
                        </button>
                      ))}
                      {catalogItems?.length === 0 && <p className="text-xs text-muted-foreground px-3 py-2">No catalog items found</p>}
                    </div>
                  )}
                  {catalogSearchOpen && <div className="fixed inset-0 z-10" onClick={() => { setCatalogSearchOpen(false); setCatalogSearch(""); }} />}
                </div>
              )}
            </div>

            <FormField control={form.control} name="name" render={({ field }) => (
              <FormItem><FormLabel>Cost Name</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <div className="grid grid-cols-3 gap-3">
              <FormField control={form.control} name="quantity" render={({ field }) => (
                <FormItem><FormLabel>Qty</FormLabel><FormControl><Input type="number" step="0.01" min="0.01" {...field} /></FormControl><FormMessage /></FormItem>
              )} />
              <FormField control={form.control} name="unit" render={({ field }) => (
                <FormItem><FormLabel>Unit</FormLabel><FormControl><Input placeholder="hr, sqft" {...field} /></FormControl></FormItem>
              )} />
              <FormField control={form.control} name="unitCost" render={({ field }) => (
                <FormItem><FormLabel>Cost ($)</FormLabel><FormControl><Input type="number" step="0.01" min="0" {...field} /></FormControl><FormMessage /></FormItem>
              )} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField control={form.control} name="supplier" render={({ field }) => (
                <FormItem><FormLabel>Supplier <span className="text-muted-foreground font-normal">(optional)</span></FormLabel><FormControl><Input {...field} /></FormControl></FormItem>
              )} />
              <FormField control={form.control} name="notes" render={({ field }) => (
                <FormItem><FormLabel>Notes <span className="text-muted-foreground font-normal">(optional)</span></FormLabel><FormControl><Input {...field} /></FormControl></FormItem>
              )} />
            </div>
            {!linkedCatalogId && (
              <FormField control={form.control} name="saveToCatalog" render={({ field }) => (
                <FormItem>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={field.value ?? false}
                      onChange={e => field.onChange(e.target.checked)}
                      className="rounded border-input"
                    />
                    <span className="text-sm text-muted-foreground">Save to cost catalog for reuse</span>
                  </label>
                </FormItem>
              )} />
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={createCost.isPending || createCatalogItem.isPending}>
                {(createCost.isPending || createCatalogItem.isPending) && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                Add Cost
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Invoice Cost Row ─────────────────────────────────────────────────────────

function InvoiceCostRow({ cost, invoiceId, lineItemId, onDelete }: { cost: Material; invoiceId: number; lineItemId: number; onDelete: () => void }) {
  const isFromProduct = !!cost.fromProductDefaultId;
  const [name, setName] = useState(cost.name || "");
  const [quantity, setQuantity] = useState(String(cost.quantity));
  const [unitCost, setUnitCost] = useState(String(cost.unitCost));
  const costType = cost.type || "materials";
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">("idle");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();
  const updateCost = useUpdateInvoiceCost();

  useEffect(() => {
    setName(cost.name || "");
    setQuantity(String(cost.quantity));
    setUnitCost(String(cost.unitCost));
  }, [cost.id, cost.name, cost.quantity, cost.unitCost]);

  const localTotal = (parseFloat(quantity) || 0) * (parseFloat(unitCost) || 0);

  const saveField = () => {
    const qty = parseFloat(quantity);
    const uc = parseFloat(unitCost);
    if (!name.trim()) { toast({ title: "Name is required", variant: "destructive" }); return; }
    if (isNaN(qty) || qty <= 0) { toast({ title: "Quantity must be > 0", variant: "destructive" }); return; }
    if (isNaN(uc) || uc < 0) { toast({ title: "Unit cost must be ≥ 0", variant: "destructive" }); return; }
    setSaveStatus("saving");
    updateCost.mutate(
      { invoiceId, lineItemId, id: cost.id, data: { name, quantity: qty, unitCost: uc, catalogItemId: cost.catalogItemId ?? null, unit: cost.unit || null, supplier: cost.supplier || null, notes: cost.notes || null, type: costType } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListInvoiceCostsQueryKey(invoiceId, lineItemId) });
          queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
          setSaveStatus("saved");
          setTimeout(() => setSaveStatus("idle"), 1500);
        },
        onError: () => {
          setSaveStatus("idle");
          toast({ title: "Failed to save cost", variant: "destructive" });
        }
      }
    );
  };

  return (
    <div className="p-3 text-sm hover:bg-muted/30 transition-colors group">
      <div className="flex items-center gap-2 flex-wrap mb-1.5">
        <Input
          value={name}
          onChange={e => setName(e.target.value)}
          onBlur={saveField}
          placeholder="Cost name"
          className="h-7 text-sm font-medium flex-1 min-w-[120px] px-2"
        />
        <span className="inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-semibold shrink-0 bg-muted/60 text-muted-foreground border-border capitalize">
          {costType === "labour" ? "labor" : costType}
        </span>
        {isFromProduct && (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 border border-blue-200 rounded font-medium cursor-default shrink-0">
                <Lock className="w-2.5 h-2.5" /> From product
              </span>
            </TooltipTrigger>
            <TooltipContent>Auto-added from product defaults. You can edit or delete it freely.</TooltipContent>
          </Tooltip>
        )}
        {saveStatus === "saving" && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground shrink-0" />}
        {saveStatus === "saved" && <span className="text-xs text-green-600 font-medium shrink-0">Saved</span>}
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <button className="p-1 text-destructive/70 hover:text-destructive shrink-0" onClick={onDelete} aria-label="Delete cost">
              <Trash2 className="w-3 h-3" />
            </button>
          </TooltipTrigger>
          <TooltipContent>Delete cost</TooltipContent>
        </Tooltip>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          <label className="text-xs text-muted-foreground shrink-0">Qty</label>
          <Input
            type="number"
            step="0.01"
            min="0.01"
            value={quantity}
            onChange={e => setQuantity(e.target.value)}
            onBlur={saveField}
            className="w-20 h-6 text-xs px-2"
          />
        </div>
        <span className="text-xs text-muted-foreground">{cost.unit || "ea"} @</span>
        <div className="flex items-center gap-1">
          <label className="text-xs text-muted-foreground shrink-0">$</label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={unitCost}
            onChange={e => setUnitCost(e.target.value)}
            onBlur={saveField}
            className="w-20 h-6 text-xs px-2"
          />
        </div>
        <span className="text-xs font-semibold ml-auto">{formatCurrency(localTotal)}</span>
      </div>
      {cost.supplier && <div className="mt-1 text-xs text-muted-foreground">Via: {cost.supplier}</div>}
      {cost.notes && <div className="mt-0.5 text-xs italic text-muted-foreground/80">{cost.notes}</div>}
    </div>
  );
}

// ─── Invoice Cost Form ────────────────────────────────────────────────────────

function InvoiceCostForm({ invoiceId, lineItemId, cost, onSuccess, onCancel }: {
  invoiceId: number;
  lineItemId: number;
  cost?: Material;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const { formatCurrency } = useSettingsFormat();
  const createCost = useCreateInvoiceCost();
  const updateCost = useUpdateInvoiceCost();
  const isEditing = !!cost;
  const [catalogSearch, setCatalogSearch] = useState("");

  const { data: catalog } = useListInternalCatalog();

  const filteredCatalog = catalog?.filter(item =>
    !catalogSearch || item.name.toLowerCase().includes(catalogSearch.toLowerCase())
  );

  const form = useForm<z.infer<typeof costSchema>>({
    resolver: zodResolver(costSchema),
    defaultValues: {
      catalogItemId: cost?.catalogItemId ?? null,
      name: cost?.name || "",
      quantity: cost?.quantity || 1,
      unit: cost?.unit || "",
      unitCost: cost?.unitCost || 0,
      supplier: cost?.supplier || "",
      notes: cost?.notes || "",
    }
  });

  const selectedCatalogId = form.watch("catalogItemId");
  const selectedCatalogItem = catalog?.find(c => c.id === Number(selectedCatalogId));

  const applyCatalogItem = (item: { id: number; name: string; unitCost: number; unit?: string | null }) => {
    form.setValue("catalogItemId", item.id);
    form.setValue("name", item.name);
    form.setValue("unitCost", Number(item.unitCost));
    form.setValue("unit", item.unit || "");
    setCatalogSearch("");
  };

  const invalidateCosts = () => {
    queryClient.invalidateQueries({ queryKey: getListInvoiceCostsQueryKey(invoiceId, lineItemId) });
    queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
  };

  const onSubmit = (data: z.infer<typeof costSchema>) => {
    const payload = {
      catalogItemId: data.catalogItemId ?? null,
      name: data.name,
      quantity: data.quantity,
      unitCost: data.unitCost,
      unit: data.unit || null,
      supplier: data.supplier || null,
      notes: data.notes || null,
    };
    if (isEditing) {
      updateCost.mutate({ invoiceId, lineItemId, id: cost.id, data: payload }, {
        onSuccess: () => { invalidateCosts(); onSuccess(); }
      });
    } else {
      createCost.mutate({ invoiceId, lineItemId, data: payload }, {
        onSuccess: () => { invalidateCosts(); onSuccess(); }
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Internal Cost" : "Add Internal Cost"}</DialogTitle>
          <DialogDescription>Track materials, labor, and subs. Private — never shown to customer.</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <label className="text-sm font-medium">Cost Catalog</label>
          {selectedCatalogItem ? (
            <div className="flex items-center justify-between p-3 rounded-md border border-primary/30 bg-primary/5">
              <div>
                <p className="text-sm font-medium">{selectedCatalogItem.name}</p>
                <p className="text-xs text-muted-foreground">{formatCurrency(Number(selectedCatalogItem.unitCost))} per {selectedCatalogItem.unit || "each"}</p>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => form.setValue("catalogItemId", null)}>Change</Button>
            </div>
          ) : (
            <div className="space-y-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search catalog or fill in manually..."
                  value={catalogSearch}
                  onChange={e => setCatalogSearch(e.target.value)}
                  className="pl-8 text-sm"
                />
              </div>
              {catalogSearch && filteredCatalog !== undefined && (
                <div className="border rounded-md shadow-sm bg-card overflow-hidden max-h-36 overflow-y-auto">
                  {filteredCatalog.map(c => (
                    <button key={c.id} type="button"
                      className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-muted/60 transition-colors border-b last:border-0"
                      onClick={() => applyCatalogItem(c)}>
                      <div>
                        <p className="text-sm font-medium">{c.name}</p>
                      </div>
                      <span className="text-sm font-semibold text-primary shrink-0 ml-2">{formatCurrency(Number(c.unitCost))}/{c.unit || "ea"}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <FormField control={form.control} name="name" render={({ field }) => (
          <FormItem><FormLabel>Item Name</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
        )} />
        <div className="grid grid-cols-3 gap-3">
          <FormField control={form.control} name="quantity" render={({ field }) => (
            <FormItem><FormLabel>Qty</FormLabel><FormControl><Input type="number" step="0.01" {...field} /></FormControl><FormMessage /></FormItem>
          )} />
          <FormField control={form.control} name="unit" render={({ field }) => (
            <FormItem><FormLabel>Unit</FormLabel><FormControl><Input placeholder="hrs, sqft" {...field} /></FormControl></FormItem>
          )} />
          <FormField control={form.control} name="unitCost" render={({ field }) => (
            <FormItem><FormLabel>Cost ($)</FormLabel><FormControl><Input type="number" step="0.01" {...field} /></FormControl><FormMessage /></FormItem>
          )} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField control={form.control} name="supplier" render={({ field }) => (
            <FormItem><FormLabel>Supplier/Vendor</FormLabel><FormControl><Input {...field} /></FormControl></FormItem>
          )} />
          <FormField control={form.control} name="notes" render={({ field }) => (
            <FormItem><FormLabel>Notes</FormLabel><FormControl><Input {...field} /></FormControl></FormItem>
          )} />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={createCost.isPending || updateCost.isPending}>Save Cost</Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

// ─── Create New Invoice Line Item Modal ───────────────────────────────────────

const createInvoiceLineItemModalSchema = z.object({
  name: z.string().min(1, "Required"),
  description: z.string().optional(),
  quantity: z.coerce.number().min(0.01, "Must be > 0").max(99999999, "Maximum value is 99,999,999"),
  unitPrice: z.coerce.number().min(-99999999.99, "Minimum value is -99,999,999").max(99999999.99, "Maximum value is 99,999,999"),
  saveAsProduct: z.boolean().optional(),
});

function CreateInvoiceLineItemModal({ invoiceId, initialName, groupId, onClose, onAdded }: {
  invoiceId: number;
  initialName: string;
  groupId?: number;
  /** Cancel: back to the search dialog. */
  onClose: () => void;
  /** Item added: close the whole add-item flow. */
  onAdded: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const createItem = useCreateInvoiceLineItem();
  const createProduct = useCreateProduct();

  const form = useForm<z.infer<typeof createInvoiceLineItemModalSchema>>({
    resolver: zodResolver(createInvoiceLineItemModalSchema),
    defaultValues: { name: initialName, description: "", quantity: 1, unitPrice: 0, saveAsProduct: false },
  });

  useEffect(() => {
    form.reset({ name: initialName, description: "", quantity: 1, unitPrice: 0, saveAsProduct: false });
  }, [initialName]);

  const onSubmit = async (data: z.infer<typeof createInvoiceLineItemModalSchema>) => {
    let productId: number | null = null;
    if (!productId && data.saveAsProduct) {
      try {
        const newProduct = await new Promise<{ id: number }>((resolve, reject) => {
          createProduct.mutate(
            { data: { name: data.name, description: data.description || null, unitPrice: data.unitPrice, unit: null } },
            { onSuccess: resolve, onError: reject }
          );
        });
        productId = newProduct.id;
        queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
      } catch {
        toast({ title: "Failed to save product", variant: "destructive" });
        return;
      }
    }

    const customerDescription = data.description?.trim() ? data.description : data.name;
    createItem.mutate(
      { invoiceId, data: { productId, description: customerDescription, quantity: data.quantity, unitPrice: data.unitPrice, groupId: groupId ?? null } },
      {
        onSuccess: (newItem) => {
          queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
          if (newItem?.id) queryClient.invalidateQueries({ queryKey: getListInvoiceCostsQueryKey(invoiceId, newItem.id) });
          toast({ title: "Line item added" });
          onAdded();
        },
        onError: (err) => toast({ title: apiErrorMessage(err, "Failed to add item"), variant: "destructive" })
      }
    );
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <DialogHeader>
              <DialogTitle>New Line Item</DialogTitle>
              <DialogDescription>Add a new service or item. Visible to the customer.</DialogDescription>
            </DialogHeader>

            <FormField control={form.control} name="name" render={({ field }) => (
              <FormItem>
                <FormLabel>Service / Item Name <span className="text-muted-foreground font-normal text-xs">(shown on invoice)</span></FormLabel>
                <FormControl><Input {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="description" render={({ field }) => (
              <FormItem>
                <FormLabel>Description <span className="text-muted-foreground font-normal">(optional detail shown to customer)</span></FormLabel>
                <FormControl><Textarea className="min-h-[60px]" placeholder="Leave blank to use the name above..." {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="quantity" render={({ field }) => (
                <FormItem>
                  <FormLabel>Quantity</FormLabel>
                  <FormControl><Input type="number" step="0.01" min="0.01" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="unitPrice" render={({ field }) => (
                <FormItem>
                  <FormLabel>Unit Price ($)</FormLabel>
                  <FormControl><Input type="number" step="0.01" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>
            <FormField control={form.control} name="saveAsProduct" render={({ field }) => (
              <FormItem>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={field.value ?? false}
                    onChange={e => field.onChange(e.target.checked)}
                    className="rounded border-input"
                  />
                  <span className="text-sm text-muted-foreground">Save as new product in catalog for reuse</span>
                </label>
              </FormItem>
            )} />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={createItem.isPending || createProduct.isPending}>
                {(createItem.isPending || createProduct.isPending) && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                Add Line Item
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Add Invoice Item Search (dialog) ─────────────────────────────────────────

function AddInvoiceItemSearch({ invoiceId, groupId, onClose }: { invoiceId: number; groupId?: number; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();
  const [search, setSearch] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalName, setCreateModalName] = useState("");
  const createItem = useCreateInvoiceLineItem();

  const { data: products } = useListProducts(
    search ? { search } : {},
    { query: { queryKey: getListProductsQueryKey(search ? { search } : {}), enabled: true } }
  );

  const addFromProduct = (product: { id: number; name: string; description?: string | null; unitPrice: number }) => {
    createItem.mutate(
      { invoiceId, data: { productId: product.id, description: product.description || product.name, quantity: 1, unitPrice: product.unitPrice, groupId: groupId ?? null } },
      {
        onSuccess: (newItem) => {
          queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
          if (newItem?.id) queryClient.invalidateQueries({ queryKey: getListInvoiceCostsQueryKey(invoiceId, newItem.id) });
          onClose();
        },
        onError: (err) => toast({ title: apiErrorMessage(err, "Failed to add item"), variant: "destructive" })
      }
    );
  };

  const openCreateModal = (name: string) => {
    setCreateModalName(name);
    setCreateModalOpen(true);
  };

  if (createModalOpen) {
    return (
      <CreateInvoiceLineItemModal
        invoiceId={invoiceId}
        initialName={createModalName}
        groupId={groupId}
        onClose={() => setCreateModalOpen(false)}
        onAdded={onClose}
      />
    );
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Add Line Item</DialogTitle>
          <DialogDescription>Search your products or create a custom item.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              autoFocus
              placeholder="Search products..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 h-9 text-sm"
              disabled={createItem.isPending}
            />
            {createItem.isPending && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-muted-foreground" />}
          </div>
          <div className="border rounded-md overflow-hidden max-h-56 overflow-y-auto">
            {products?.map(p => (
              <button
                key={p.id}
                type="button"
                className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-muted/60 transition-colors border-b last:border-0"
                onClick={() => addFromProduct(p)}
              >
                <div>
                  <p className="text-sm font-medium">{p.name}</p>
                  {p.description && <p className="text-xs text-muted-foreground truncate max-w-[220px]">{p.description}</p>}
                </div>
                <span className="text-sm font-semibold text-primary shrink-0 ml-2">{formatCurrency(Number(p.unitPrice))}</span>
              </button>
            ))}
            {search.trim() && !products?.some(p => p.name.toLowerCase() === search.trim().toLowerCase()) && (
              <button
                type="button"
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-muted/60 transition-colors text-sm text-primary font-medium border-t"
                onClick={() => openCreateModal(search.trim())}
              >
                <Plus className="w-3.5 h-3.5 shrink-0" />
                Create new service: "{search.trim()}"
              </button>
            )}
            {!search && (!products || products.length === 0) && (
              <p className="text-xs text-muted-foreground px-3 py-3">Type to search products, or a name to create a custom item.</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── New Line Item Dialog ─────────────────────────────────────────────────────

function NewLineItemDialog({ invoiceId, open, onClose }: { invoiceId: number; open: boolean; onClose: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { formatCurrency } = useSettingsFormat();
  const { data: products } = useListProducts();
  const [productSearch, setProductSearch] = useState("");
  const createItem = useCreateInvoiceLineItem();

  const form = useForm<z.infer<typeof lineItemSchema>>({
    resolver: zodResolver(lineItemSchema),
    defaultValues: { productId: null, description: "", quantity: 1, unitPrice: 0 },
  });

  const onSubmit = (data: z.infer<typeof lineItemSchema>) => {
    createItem.mutate({ invoiceId, data: { ...data, productId: data.productId ?? null } }, {
      onSuccess: (newItem) => {
        queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
        if (data.productId && newItem?.id) {
          queryClient.invalidateQueries({ queryKey: getListInvoiceCostsQueryKey(invoiceId, newItem.id) });
        }
        form.reset({ productId: null, description: "", quantity: 1, unitPrice: 0 });
        onClose();
        toast({ title: "Line item added" });
      },
      onError: (err) => toast({ title: apiErrorMessage(err, "Failed to add line item"), variant: "destructive" }),
    });
  };

  const filteredProducts = products?.filter(p =>
    !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Add Line Item</DialogTitle></DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <p className="text-xs text-muted-foreground mb-2">Quick-fill from a product (optional):</p>
              <div className="relative mb-2">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input placeholder="Search products..." className="pl-8 h-8 text-sm" value={productSearch} onChange={e => setProductSearch(e.target.value)} />
              </div>
              {productSearch && filteredProducts && filteredProducts.length > 0 && (
                <div className="border rounded-md bg-card max-h-32 overflow-y-auto text-xs mb-2">
                  {filteredProducts.map(p => (
                    <button key={p.id} type="button" className="w-full text-left px-3 py-2 hover:bg-muted/50 flex justify-between gap-2"
                      onClick={() => {
                        form.setValue("productId", p.id);
                        form.setValue("description", p.description || p.name);
                        form.setValue("unitPrice", p.unitPrice ?? 0);
                        setProductSearch("");
                      }}>
                      <span className="font-medium">{p.name}</span>
                      <span className="text-muted-foreground">{formatCurrency(p.unitPrice ?? 0)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <FormField control={form.control} name="description" render={({ field }) => (
              <FormItem><FormLabel>Description</FormLabel><FormControl><Textarea {...field} className="min-h-[80px]" /></FormControl><FormMessage /></FormItem>
            )} />
            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="quantity" render={({ field }) => (
                <FormItem><FormLabel>Quantity</FormLabel><FormControl><Input type="number" step="0.01" min="0.01" {...field} /></FormControl><FormMessage /></FormItem>
              )} />
              <FormField control={form.control} name="unitPrice" render={({ field }) => (
                <FormItem><FormLabel>Unit Price ($)</FormLabel><FormControl><Input type="number" step="0.01" {...field} /></FormControl><FormMessage /></FormItem>
              )} />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={createItem.isPending}>
                {createItem.isPending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />} Add Item
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Tax helpers ──────────────────────────────────────────────────────────────

const parseTaxLinesSafe = parseSelectedTaxLines;

// ─── Pricing Summary (inline tax checkboxes, matching estimate style) ─────────

function PricingSummary({ invoice, invoiceId, formatCurrency, companySettings, readOnly = false }: {
  invoice: InvoiceDetail;
  invoiceId: number;
  formatCurrency: (v: number) => string;
  companySettings: CompanySettings | undefined;
  readOnly?: boolean;
}) {
  const queryClient = useQueryClient();
  const updateInvoice = useUpdateInvoice();
  const { toast } = useToast();

  const taxEnabled: boolean = invoice.taxEnabled ?? false;
  const subtotal: number = invoice.subtotal ?? 0;

  const settingsTaxLines = parseTaxLinesSafe(companySettings?.taxLines);
  const [localTaxEnabled, setLocalTaxEnabled] = useState(taxEnabled);
  const [localSelectedLines, setLocalSelectedLines] = useState<TaxLine[]>(
    parseTaxLinesSafe(invoice.selectedTaxLines)
  );

  const initialDiscountEnabled = (invoice.discountValue ?? 0) > 0 && !!invoice.discountType;
  const [discountEnabled, setDiscountEnabled] = useState(initialDiscountEnabled);
  const [discountType, setDiscountType] = useState<"percent" | "fixed">(
    (invoice.discountType as "percent" | "fixed" | null) ?? "percent"
  );
  const [discountInput, setDiscountInput] = useState<string>(
    invoice.discountValue != null ? String(invoice.discountValue) : ""
  );

  useEffect(() => {
    setLocalTaxEnabled(invoice.taxEnabled ?? false);
    setLocalSelectedLines(parseTaxLinesSafe(invoice.selectedTaxLines));
  }, [invoice.taxEnabled, invoice.selectedTaxLines]);

  useEffect(() => {
    const enabled = (invoice.discountValue ?? 0) > 0 && !!invoice.discountType;
    setDiscountEnabled(enabled);
    setDiscountType((invoice.discountType as "percent" | "fixed" | null) ?? "percent");
    setDiscountInput(invoice.discountValue != null ? String(invoice.discountValue) : "");
  }, [invoice.discountType, invoice.discountValue]);

  const parsedDiscountValue = (() => {
    const n = Number(discountInput);
    if (!Number.isFinite(n) || n < 0) return 0;
    if (discountType === "percent") return Math.min(n, 100);
    return n;
  })();
  const previewDiscountAmount = discountEnabled && parsedDiscountValue > 0
    ? Math.max(0, Math.min(
        discountType === "percent" ? subtotal * (parsedDiscountValue / 100) : parsedDiscountValue,
        subtotal,
      ))
    : 0;
  const taxableBase = subtotal - previewDiscountAmount;

  const legacyTaxRate: number = (invoice.taxRate as unknown as number) ?? 0;
  // Registration numbers come from Settings, not the stored snapshot, so a
  // corrected tax ID shows up on existing invoices instead of the old number.
  const activeTaxLines = reconcileTaxLineIds(localSelectedLines, companySettings?.taxLines);
  // Mirror computeTaxAmount's sentinel logic: if selectedTaxLines was explicitly
  // configured (even cleared to "[]"), use the local array (empty → $0 tax).
  // Only fall back to legacyTaxRate when lines were never configured (null/"").
  const hasConfiguredLines =
    invoice.selectedTaxLines != null && invoice.selectedTaxLines !== "";
  const previewTaxAmount = localTaxEnabled
    ? (hasConfiguredLines || activeTaxLines.length > 0
        ? activeTaxLines.reduce((sum, tl) => sum + taxableBase * (tl.rate / 100), 0)
        : taxableBase * (legacyTaxRate / 100))
    : 0;
  const previewTotal = taxableBase + previewTaxAmount;

  const saveToServer = (enabled: boolean, lines: TaxLine[]) => {
    updateInvoice.mutate(
      { id: invoiceId, data: { taxEnabled: enabled, selectedTaxLines: JSON.stringify(lines) } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
          queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
          toast({ title: "Tax settings saved" });
        },
        onError: () => {
          toast({ title: "Failed to save tax settings", variant: "destructive" });
        },
      }
    );
  };

  const saveDiscount = (type: "percent" | "fixed" | null, value: number | null) => {
    updateInvoice.mutate(
      { id: invoiceId, data: { discountType: type, discountValue: value } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
          queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
          toast({ title: "Discount saved" });
        },
        onError: () => {
          toast({ title: "Failed to save discount", variant: "destructive" });
        },
      }
    );
  };

  const handleDiscountToggle = () => {
    const next = !discountEnabled;
    setDiscountEnabled(next);
    if (!next) {
      saveDiscount(null, null);
    } else if (parsedDiscountValue > 0) {
      saveDiscount(discountType, parsedDiscountValue);
    }
  };

  const handleDiscountTypeChange = (next: "percent" | "fixed") => {
    setDiscountType(next);
    if (discountEnabled && parsedDiscountValue > 0) {
      const clamped = next === "percent" ? Math.min(parsedDiscountValue, 100) : parsedDiscountValue;
      setDiscountInput(String(clamped));
      saveDiscount(next, clamped);
    }
  };

  const commitDiscountValue = () => {
    if (!discountEnabled) return;
    const n = Number(discountInput);
    if (!Number.isFinite(n) || n < 0) {
      saveDiscount(null, null);
      setDiscountInput("");
      return;
    }
    const clamped = discountType === "percent" ? Math.min(n, 100) : n;
    if (clamped !== n) setDiscountInput(String(clamped));
    if (clamped <= 0) {
      saveDiscount(null, null);
    } else {
      saveDiscount(discountType, clamped);
    }
  };

  const handleToggle = () => {
    const next = !localTaxEnabled;
    setLocalTaxEnabled(next);
    saveToServer(next, localSelectedLines);
  };

  const handleLineToggle = (line: TaxLine) => {
    const isSelected = localSelectedLines.some(l => l.name === line.name && l.rate === line.rate);
    const next = isSelected
      ? localSelectedLines.filter(l => !(l.name === line.name && l.rate === line.rate))
      : [...localSelectedLines, line];
    setLocalSelectedLines(next);
    saveToServer(localTaxEnabled, next);
  };

  const showBreakdown = localTaxEnabled || previewDiscountAmount > 0;

  return (
    <div className="p-4 bg-muted/10 border-t space-y-2">
      {!readOnly && (<>
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={handleDiscountToggle}
          disabled={updateInvoice.isPending}
          className={cn(
            "relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus-visible:outline-none",
            discountEnabled ? "bg-primary" : "bg-input"
          )}
          aria-label="Toggle discount"
        >
          <span className={cn(
            "inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform",
            discountEnabled ? "translate-x-4" : "translate-x-0.5"
          )} />
        </button>
        <span className="text-sm font-medium text-muted-foreground">Apply Discount</span>
      </div>

      {discountEnabled && (
        <div className="mb-3 flex items-center gap-2">
          <div className="inline-flex rounded-md border border-input overflow-hidden">
            <button
              type="button"
              onClick={() => handleDiscountTypeChange("percent")}
              className={cn(
                "px-2 py-1 text-xs flex items-center gap-1",
                discountType === "percent" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"
              )}
              aria-label="Percent discount"
            >
              <Percent className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={() => handleDiscountTypeChange("fixed")}
              className={cn(
                "px-2 py-1 text-xs flex items-center gap-1 border-l border-input",
                discountType === "fixed" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"
              )}
              aria-label="Fixed discount"
            >
              <DollarSign className="w-3 h-3" />
            </button>
          </div>
          <input
            type="number"
            min={0}
            max={discountType === "percent" ? 100 : undefined}
            step="0.01"
            value={discountInput}
            onChange={(e) => setDiscountInput(e.target.value)}
            onBlur={commitDiscountValue}
            onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
            placeholder={discountType === "percent" ? "0–100" : "0.00"}
            className="w-24 px-2 py-1 text-sm border border-input rounded-md bg-background"
          />
          <span className="text-xs text-muted-foreground">
            {discountType === "percent" ? "% off" : "off"}
          </span>
        </div>
      )}

      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={handleToggle}
          disabled={updateInvoice.isPending}
          className={cn(
            "relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus-visible:outline-none",
            localTaxEnabled ? "bg-primary" : "bg-input"
          )}
          aria-label="Toggle tax"
        >
          <span className={cn(
            "inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform",
            localTaxEnabled ? "translate-x-4" : "translate-x-0.5"
          )} />
        </button>
        <span className="text-sm font-medium text-muted-foreground">Charge Tax</span>
        {updateInvoice.isPending && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
      </div>

      {localTaxEnabled && (
        <div className="mb-3">
          {settingsTaxLines.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">
              No tax lines configured.{" "}
              <a href="/settings" className="underline text-primary">Go to Settings</a>{" "}
              to add tax lines.
            </p>
          ) : (
            <div className="space-y-1.5">
              {settingsTaxLines.map((line, idx) => {
                const isChecked = localSelectedLines.some(l => l.name === line.name && l.rate === line.rate);
                return (
                  <label key={idx} className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleLineToggle(line)}
                      disabled={updateInvoice.isPending}
                      className="rounded border-input"
                    />
                    <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                      {line.name} ({line.rate}%){line.taxId ? ` — ${line.taxId}` : ""}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}
      </>)}

      {showBreakdown ? (
        <div className="space-y-1.5">
          <div className="flex justify-between items-center text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1">Subtotal <InfoTip content={FORMULAS.subtotal} /></span>
            <span>{formatCurrency(subtotal)}</span>
          </div>
          {previewDiscountAmount > 0 && (
            <div className="flex justify-between items-center text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                Discount{discountType === "percent" ? ` (${parsedDiscountValue}%)` : ""} <InfoTip content={FORMULAS.discount} />
              </span>
              <span>-{formatCurrency(previewDiscountAmount)}</span>
            </div>
          )}
          {localTaxEnabled && (activeTaxLines.length > 0 ? (
            activeTaxLines.map((tl, idx) => (
              <div key={idx} className="flex justify-between items-center text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1">{tl.name} ({tl.rate}%){tl.taxId ? ` — ${tl.taxId}` : ""}{idx === 0 ? <InfoTip content={FORMULAS.tax} /> : null}</span>
                <span>{formatCurrency(taxableBase * (tl.rate / 100))}</span>
              </div>
            ))
          ) : legacyTaxRate > 0 ? (
            <div className="flex justify-between items-center text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1">Tax ({legacyTaxRate}%) <InfoTip content={FORMULAS.tax} /></span>
              <span>{formatCurrency(previewTaxAmount)}</span>
            </div>
          ) : null)}
          <div className="flex justify-between items-center pt-1 border-t">
            <span className="font-semibold inline-flex items-center gap-1">Total <InfoTip content={FORMULAS.total} /></span>
            <span className="text-2xl font-bold">{formatCurrency(previewTotal)}</span>
          </div>
        </div>
      ) : (
        <div className="flex justify-between items-center">
          <span className="font-semibold text-muted-foreground">Total Customer Price</span>
          <span className="text-2xl font-bold">{formatCurrency(previewTotal)}</span>
        </div>
      )}
    </div>
  );
}

// ─── Payment Plan Section (unified Deposit + Milestones) ───────────────

const markDepositReceivedSchema = z.object({
  amount: z.coerce.number().min(0.01, "Amount must be greater than 0"),
  paymentMethod: z.string().nullable().optional(),
  paymentDate: z.string().min(1, "Date is required"),
  notes: z.string().nullable().optional(),
});
type MarkDepositReceivedForm = z.infer<typeof markDepositReceivedSchema>;

function DepositInvoiceAction({
  invoice,
  invoiceId,
  depositAmount,
  formatCurrency,
}: {
  invoice: InvoiceDetail;
  invoiceId: number;
  depositAmount: number;
  formatCurrency: (v: number) => string;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const createDepositInvoice = useCreateDepositInvoice();
  const markDepositReceived = useMarkDepositReceived();
  const [markDialogOpen, setMarkDialogOpen] = useState(false);

  const markForm = useForm<MarkDepositReceivedForm>({
    resolver: zodResolver(markDepositReceivedSchema),
    defaultValues: {
      amount: depositAmount,
      paymentMethod: null,
      paymentDate: new Date().toISOString().slice(0, 10),
      notes: null,
    },
  });

  useEffect(() => {
    if (markDialogOpen) {
      markForm.reset({
        amount: depositAmount,
        paymentMethod: null,
        paymentDate: new Date().toISOString().slice(0, 10),
        notes: null,
      });
    }
  }, [markDialogOpen, depositAmount, markForm]);

  const childId = invoice.depositChildId ?? null;
  const childStatus = invoice.depositChildStatus ?? null;
  const childPaidDate = invoice.depositChildPaidDate ?? null;

  const handleCreate = () => {
    createDepositInvoice.mutate(
      { parentInvoiceId: invoiceId },
      {
        onSuccess: (created) => {
          queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
          queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
          toast({ title: "Deposit invoice created — opening it now" });
          setLocation(`/invoices/${created.id}`);
        },
        onError: (err: unknown) => {
          const e = err as { response?: { data?: { error?: string } }; message?: string };
          toast({ title: e?.response?.data?.error || "Failed to create deposit invoice", variant: "destructive" });
        },
      }
    );
  };

  const handleMarkReceived = (values: MarkDepositReceivedForm) => {
    markDepositReceived.mutate(
      {
        invoiceId,
        data: {
          amount: values.amount,
          paymentMethod: values.paymentMethod ?? null,
          paymentDate: values.paymentDate,
          notes: values.notes ?? null,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
          queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
          queryClient.invalidateQueries({ queryKey: getListInvoicePaymentsQueryKey(invoiceId) });
          setMarkDialogOpen(false);
          toast({ title: "Deposit marked as received" });
        },
        onError: (err: unknown) => {
          const e = err as { response?: { data?: { error?: string } }; message?: string };
          toast({ title: e?.response?.data?.error || "Failed to mark deposit as received", variant: "destructive" });
        },
      }
    );
  };

  if (childId) {
    const statusLabel = childStatus === "paid"
      ? "Paid"
      : childStatus === "void"
      ? "Void"
      : childStatus === "partial"
      ? "Partially Paid"
      : childStatus === "sent"
      ? "Sent"
      : childStatus === "overdue"
      ? "Overdue"
      : "Draft";
    const isPaid = childStatus === "paid";
    const isVoid = childStatus === "void";

    return (
      <div className={cn(
        "rounded-md border px-3 py-2.5 text-xs space-y-2",
        isPaid ? "border-emerald-200 bg-emerald-50" : isVoid ? "border-muted bg-muted/40" : "border-blue-200 bg-blue-50",
      )}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-medium flex items-center gap-1.5">
              <Receipt className="w-3.5 h-3.5" />
              Deposit Invoice
            </div>
            <div className="text-muted-foreground mt-0.5">
              {/* Show the child's ACTUAL billed amount (frozen once sent), not a
                  figure recomputed live from the parent. */}
              {formatCurrency(invoice.depositChildAmount ?? depositAmount)} · {statusLabel}
              {isPaid && childPaidDate ? ` on ${childPaidDate}` : ""}
            </div>
          </div>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setLocation(`/invoices/${childId}`)}>
            Open
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
        {isVoid && (
          <Button
            size="sm"
            variant="outline"
            className="w-full h-7 text-xs"
            onClick={handleCreate}
            disabled={createDepositInvoice.isPending}
          >
            {createDepositInvoice.isPending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <PlusCircle className="w-3 h-3 mr-1" />}
            Create New Deposit Invoice
          </Button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border border-border/60 bg-muted/30 px-3 py-2.5 text-xs space-y-2">
        <p className="text-muted-foreground">
          Send a separate {formatCurrency(depositAmount)} deposit invoice, or mark it as already received.
        </p>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="flex-1 h-7 text-xs"
            onClick={handleCreate}
            disabled={createDepositInvoice.isPending || markDepositReceived.isPending}
          >
            {createDepositInvoice.isPending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />}
            Create Invoice
          </Button>
          <Button
            size="sm"
            className="flex-1 h-7 text-xs"
            onClick={() => setMarkDialogOpen(true)}
            disabled={createDepositInvoice.isPending || markDepositReceived.isPending}
          >
            <CheckCircle className="w-3 h-3 mr-1" />
            Mark as Received
          </Button>
        </div>
      </div>

      <Dialog open={markDialogOpen} onOpenChange={setMarkDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Deposit as Received</DialogTitle>
            <DialogDescription>
              Record that you have collected the {formatCurrency(depositAmount)} deposit outside the platform (e.g. check, e-transfer).
            </DialogDescription>
          </DialogHeader>
          <Form {...markForm}>
            <form onSubmit={markForm.handleSubmit(handleMarkReceived)} className="space-y-4">
              <FormField
                control={markForm.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" min="0.01" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={markForm.control}
                name="paymentMethod"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Payment Method</FormLabel>
                    <Select
                      onValueChange={(v) => field.onChange(v === "__none" ? null : v)}
                      value={field.value ?? "__none"}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select method" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__none">Not specified</SelectItem>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="cheque">Check</SelectItem>
                        <SelectItem value="e-transfer">E-Transfer</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="credit_card">Credit Card</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={markForm.control}
                name="paymentDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date Received</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={markForm.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes (optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="e.g. Check #1234"
                        rows={2}
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setMarkDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={markDepositReceived.isPending}>
                  {markDepositReceived.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Mark as Received
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Read-only status for the "remaining balance" invoice — materialized as a
 * draft automatically alongside the deposit invoice as soon as a deposit is
 * set (see materializeContainer on the server). There's no "create" action
 * here; the contractor sends it themselves once work is done, at which point
 * it becomes a normal invoice with its own due date and overdue lifecycle.
 */
function BalanceInvoiceStatus({
  invoice,
  formatCurrency,
}: {
  invoice: InvoiceDetail;
  formatCurrency: (v: number) => string;
}) {
  const [, setLocation] = useLocation();
  const childId = invoice.balanceChildId ?? null;
  const childStatus = invoice.balanceChildStatus ?? null;
  const childAmount = invoice.balanceChildAmount ?? null;
  if (!childId) return null;

  const statusLabel = childStatus === "paid"
    ? "Paid"
    : childStatus === "void"
    ? "Void"
    : childStatus === "partial"
    ? "Partially Paid"
    : childStatus === "sent"
    ? "Sent"
    : childStatus === "overdue"
    ? "Overdue"
    : "Draft";
  const isPaid = childStatus === "paid";
  const isVoid = childStatus === "void";

  return (
    <div className={cn(
      "rounded-md border px-3 py-2.5 text-xs space-y-2",
      isPaid ? "border-emerald-200 bg-emerald-50" : isVoid ? "border-muted bg-muted/40" : "border-blue-200 bg-blue-50",
    )}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-medium flex items-center gap-1.5">
            <Receipt className="w-3.5 h-3.5" />
            Remaining Balance Invoice
          </div>
          <div className="text-muted-foreground mt-0.5">
            {childAmount != null ? `${formatCurrency(childAmount)} · ` : ""}{statusLabel}
            {childStatus === "draft" && " — send once work is done"}
          </div>
        </div>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setLocation(`/invoices/${childId}`)}>
          Open
          <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </div>
    </div>
  );
}

function InvoicePaymentPlanSection({
  invoice,
  invoiceId,
  formatCurrency,
  invoiceMilestones,
  isRecurringActive = false,
  onLocalModeChange,
}: {
  invoice: InvoiceDetail;
  invoiceId: number;
  formatCurrency: (v: number) => string;
  invoiceMilestones: MilestoneItem[];
  isRecurringActive?: boolean;
  onLocalModeChange?: (mode: PaymentMode) => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const { formatCurrency: formatCurrencyLocal } = useSettingsFormat();
  const { user } = useAuth();
  const { hasProAccess: isPro } = getTrialInfo(user);

  const isChild = !!invoice.milestoneParentId;

  // ── Shared: mutation hooks for mode-switching clears ────────────────────────
  const updateInvoiceForMode = useUpdateInvoice();
  const upsertMilestonesForMode = useUpsertMilestones();

  // ── Derive current mode from data ───────────────────────────────────────────
  const deriveMode = (): PaymentMode => {
    if (isChild || (invoiceMilestones?.length ?? 0) > 0) return "progress";
    if ((invoice.depositValue ?? 0) > 0) return "deposit";
    return "none";
  };

  const [mode, setMode] = useState<PaymentMode>(deriveMode());
  const [isModeChangePending, setIsModeChangePending] = useState(false);

  useEffect(() => {
    const derived = deriveMode();
    setMode(derived);
    onLocalModeChange?.(derived);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice.depositValue, invoiceMilestones?.length, invoice.milestoneParentId]);

  const handleModeChange = (newMode: PaymentMode) => {
    if (newMode === mode || isModeChangePending) return;
    if (newMode !== "none" && isRecurringActive) return;
    onLocalModeChange?.(newMode);
    const prevMode = mode;

    const needsClearDeposit = newMode !== "deposit" && (invoice.depositValue ?? 0) > 0;
    const needsClearMilestones = newMode !== "progress" && (invoiceMilestones?.length ?? 0) > 0;

    // Guard: check milestone locks before committing any change
    if (needsClearMilestones) {
      const lockedMilestones = invoiceMilestones.filter((m: MilestoneItem) => m.status !== "pending");
      if (lockedMilestones.length > 0) {
        toast({
          title: "Cannot clear milestones",
          description: `${lockedMilestones.length} milestone${lockedMilestones.length > 1 ? "s have" : " has"} already been invoiced or paid.`,
          variant: "destructive",
        });
        return;
      }
    }

    setMode(newMode);

    // Auto-seed first template when switching to progress with no persisted milestones
    if (newMode === "progress" && (invoiceMilestones?.length ?? 0) === 0) {
      setMilestoneRows(
        MILESTONE_TEMPLATES[0].milestones.map(m => ({
          name: m.name,
          percentage: String(m.percentage),
          amount: "",
          usePercent: true,
        }))
      );
      setIsMilestoneDirty(true);
    }

    if (!needsClearDeposit && !needsClearMilestones) return;

    setIsModeChangePending(true);

    if (needsClearMilestones) {
      upsertMilestonesForMode.mutate(
        { invoiceId, data: { milestones: [] } },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListMilestonesQueryKey(invoiceId) });
            queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
            if (needsClearDeposit) {
              updateInvoiceForMode.mutate(
                { id: invoiceId, data: { depositType: "percent", depositValue: 0 } },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
                    queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
                    setIsModeChangePending(false);
                    toast({ title: "Milestones cleared" });
                  },
                  onError: () => {
                    setIsModeChangePending(false);
                    toast({ title: "Failed to clear deposit", variant: "destructive" });
                  },
                }
              );
            } else {
              setIsModeChangePending(false);
              toast({ title: "Milestones cleared" });
            }
          },
          onError: () => {
            setMode(prevMode);
            setIsModeChangePending(false);
            toast({ title: "Failed to clear milestones", variant: "destructive" });
          },
        }
      );
    } else if (needsClearDeposit) {
      updateInvoiceForMode.mutate(
        { id: invoiceId, data: { depositType: "percent", depositValue: 0 } },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
            queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
            setIsModeChangePending(false);
          },
          onError: () => {
            setMode(prevMode);
            setIsModeChangePending(false);
            toast({ title: "Failed to clear deposit", variant: "destructive" });
          },
        }
      );
    }
  };

  // ── Deposit form state ───────────────────────────────────────────────────────
  const updateInvoiceDeposit = useUpdateInvoice();
  const depositType: "fixed" | "percent" = (invoice.depositType as "fixed" | "percent") ?? "percent";
  const depositValue: number = invoice.depositValue ?? 0;
  const total = invoice.total ?? 0;

  const computedDepositAmount = (() => {
    if (depositValue <= 0) return 0;
    if (depositType === "fixed") return depositValue;
    return total * (depositValue / 100);
  })();

  const [localDepositType, setLocalDepositType] = useState<"fixed" | "percent">(depositType);
  const [localDepositValue, setLocalDepositValue] = useState<string>(depositValue === 0 ? "" : String(depositValue));
  const [isDepositDirty, setIsDepositDirty] = useState(false);

  useEffect(() => {
    setLocalDepositType(invoice.depositType ?? "percent");
    setLocalDepositValue(invoice.depositValue === 0 || invoice.depositValue == null ? "" : String(invoice.depositValue));
    setIsDepositDirty(false);
  }, [invoice.depositType, invoice.depositValue]);

  const depositPreviewAmount = (() => {
    const v = parseFloat(localDepositValue) || 0;
    if (v <= 0) return 0;
    if (localDepositType === "fixed") return v;
    return total * (v / 100);
  })();

  const handleDepositSave = () => {
    const v = parseFloat(localDepositValue) || 0;
    updateInvoiceDeposit.mutate(
      { id: invoiceId, data: { depositType: localDepositType, depositValue: v } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
          queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
          setIsDepositDirty(false);
          toast({ title: "Deposit saved" });
        },
        onError: () => {
          toast({ title: "Failed to save deposit", variant: "destructive" });
        },
      }
    );
  };

  // ── Milestone form state ─────────────────────────────────────────────────────
  const upsertMilestonesMutation = useUpsertMilestones();
  const completeMilestoneMutation = useCompleteMilestone();
  const [milestoneRows, setMilestoneRows] = useState<MilestoneRow[]>([]);
  const [isMilestoneDirty, setIsMilestoneDirty] = useState(false);
  const [completingId, setCompletingId] = useState<number | null>(null);
  const dragIndex = useRef<number | null>(null);

  const { data: milestonesData, isLoading: isMilestonesLoading } = useGetMilestones(invoiceId, {
    query: { queryKey: getListMilestonesQueryKey(invoiceId), enabled: mode === "progress" },
  });

  useEffect(() => {
    if (milestonesData && milestonesData.length > 0 && !isMilestoneDirty) {
      setMilestoneRows(
        milestonesData.map((m: MilestoneItem) => ({
          id: m.id,
          name: m.name,
          percentage: m.percentage !== null && m.percentage !== undefined ? String(m.percentage) : "",
          amount: m.amount !== null && m.amount !== undefined ? String(m.amount) : "",
          usePercent: m.percentage !== null && m.percentage !== undefined,
          locked: m.status !== "pending",
        }))
      );
    }
  }, [milestonesData, isMilestoneDirty]);

  const applyMilestoneTemplate = (template: typeof MILESTONE_TEMPLATES[number]) => {
    setMilestoneRows(
      template.milestones.map(m => ({
        name: m.name,
        percentage: String(m.percentage),
        amount: "",
        usePercent: true,
      }))
    );
    setIsMilestoneDirty(true);
  };

  const addMilestoneRow = () => {
    setMilestoneRows(r => [...r, { name: "", percentage: "", amount: "", usePercent: true }]);
    setIsMilestoneDirty(true);
  };

  const updateMilestoneRow = (idx: number, field: keyof MilestoneRow, value: string | boolean) => {
    setMilestoneRows(r => r.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
    setIsMilestoneDirty(true);
  };

  const removeMilestoneRow = (idx: number) => {
    setMilestoneRows(r => {
      if (r[idx]?.locked) return r;
      return r.filter((_, i) => i !== idx);
    });
    setIsMilestoneDirty(true);
  };

  const handleMilestoneDragStart = (idx: number) => { dragIndex.current = idx; };
  const handleMilestoneDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    const from = dragIndex.current;
    if (from === null || from === idx) return;
    setMilestoneRows(r => {
      const next = [...r];
      const [moved] = next.splice(from, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    dragIndex.current = idx;
    setIsMilestoneDirty(true);
  };
  const handleMilestoneDragEnd = () => { dragIndex.current = null; };

  const milestoneTotalPct = milestoneRows.reduce((s, r) => s + (r.usePercent ? (parseFloat(r.percentage) || 0) : 0), 0);
  const milestoneRoundedTotal = Math.round(milestoneTotalPct * 100) / 100;
  const milestonePctRows = milestoneRows.filter(r => r.usePercent);
  const milestoneFixedRows = milestoneRows.filter(r => !r.usePercent);
  const milestoneAllPctMode = milestonePctRows.length > 0 && milestoneFixedRows.length === 0;
  const milestoneIsComplete = milestonePctRows.length === 0 || Math.abs(milestoneRoundedTotal - 100) < 0.01;
  const milestoneIsValidPct = milestoneAllPctMode ? milestoneIsComplete : milestoneRoundedTotal <= 100;

  const invoicedCount = milestonesData?.filter((m: MilestoneItem) => m.status === "invoiced" || m.status === "paid").length ?? 0;
  const totalMilestones = milestonesData?.length ?? 0;
  const billedAmount = milestonesData?.reduce((s: number, m: MilestoneItem) => {
    if (m.status === "invoiced" || m.status === "paid") {
      if (m.amount !== null && m.amount !== undefined) return s + m.amount;
      if (m.percentage !== null && m.percentage !== undefined) {
        return s + Math.round(invoice.total * (m.percentage / 100) * 100) / 100;
      }
    }
    return s;
  }, 0) ?? 0;

  const handleMilestoneSave = () => {
    if (milestoneAllPctMode && !milestoneIsComplete) {
      toast({ title: `Percentages must total exactly 100% (currently ${milestoneRoundedTotal.toFixed(1)}%)`, variant: "destructive" });
      return;
    }
    if (!milestoneIsValidPct) {
      toast({ title: `Percentages sum to ${milestoneRoundedTotal}%, which exceeds 100%`, variant: "destructive" });
      return;
    }
    const emptyName = milestoneRows.findIndex(r => !r.name.trim());
    if (emptyName !== -1) {
      toast({ title: `Milestone ${emptyName + 1} needs a name`, variant: "destructive" });
      return;
    }
    const emptyValue = milestoneRows.findIndex(r => {
      if (r.usePercent) return !r.percentage || isNaN(parseFloat(r.percentage));
      return !r.amount || isNaN(parseFloat(r.amount));
    });
    if (emptyValue !== -1) {
      toast({ title: `Milestone ${emptyValue + 1} needs a ${milestoneRows[emptyValue].usePercent ? "percentage" : "fixed amount"}`, variant: "destructive" });
      return;
    }

    upsertMilestonesMutation.mutate(
      {
        invoiceId,
        data: {
          milestones: milestoneRows.map((r, i) => ({
            ...(r.id !== undefined ? { id: r.id } : {}),
            name: r.name.trim(),
            percentage: r.usePercent && r.percentage ? parseFloat(r.percentage) : null,
            amount: !r.usePercent && r.amount ? parseFloat(r.amount) : null,
            sortOrder: i,
          })),
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListMilestonesQueryKey(invoiceId) });
          queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
          setIsMilestoneDirty(false);
          toast({ title: "Milestones saved" });
        },
        onError: (err: unknown) => {
          const e = err as { response?: { data?: { error?: string } }; message?: string };
          toast({ title: e?.response?.data?.error || "Failed to save milestones", variant: "destructive" });
        },
      }
    );
  };

  const handleComplete = (milestone: MilestoneItem) => {
    setCompletingId(milestone.id);
    completeMilestoneMutation.mutate(
      { invoiceId, milestoneId: milestone.id },
      {
        onSuccess: (result) => {
          queryClient.invalidateQueries({ queryKey: getListMilestonesQueryKey(invoiceId) });
          queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
          queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
          setCompletingId(null);
          toast({ title: "Milestone invoiced — opening child invoice" });
          setLocation(`/invoices/${result.childInvoiceId}`);
        },
        onError: (err: unknown) => {
          const e = err as { response?: { data?: { error?: string } }; message?: string };
          setCompletingId(null);
          toast({ title: e?.response?.data?.error || "Failed to complete milestone", variant: "destructive" });
        },
      }
    );
  };

  // ── Deposit content ──────────────────────────────────────────────────────────
  const depositContent = (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">Set a deposit for this invoice. Leave blank or 0 to hide from the PDF.</p>
      <div className="flex gap-1 p-1 bg-border rounded-lg w-full">
        <button
          type="button"
          onClick={() => { setLocalDepositType("fixed"); setIsDepositDirty(true); }}
          className={cn(
            "flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-xs font-medium transition-colors",
            localDepositType === "fixed" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
          )}
        >
          <DollarSign className="w-3 h-3" /> Fixed ($)
        </button>
        <button
          type="button"
          onClick={() => { setLocalDepositType("percent"); setIsDepositDirty(true); }}
          className={cn(
            "flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-xs font-medium transition-colors",
            localDepositType === "percent" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
          )}
        >
          <Percent className="w-3 h-3" /> Percent (%)
        </button>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
            {localDepositType === "fixed" ? "$" : "%"}
          </span>
          <Input
            type="number"
            step={localDepositType === "fixed" ? "0.01" : "0.1"}
            min="0"
            max={localDepositType === "percent" ? "100" : undefined}
            placeholder={localDepositType === "fixed" ? "500.00" : "25"}
            value={localDepositValue}
            onChange={e => { setLocalDepositValue(e.target.value); setIsDepositDirty(true); }}
            className="pl-7 text-sm h-8"
          />
        </div>
        {localDepositType === "percent" && parseFloat(localDepositValue) > 0 && total > 0 && (
          <span className="text-xs text-muted-foreground shrink-0">
            = {formatCurrency(depositPreviewAmount)}
          </span>
        )}
      </div>

      {isDepositDirty && (
        <Button size="sm" className="w-full h-7 text-xs" onClick={handleDepositSave} disabled={updateInvoiceDeposit.isPending}>
          {updateInvoiceDeposit.isPending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
          Save Deposit
        </Button>
      )}

      {/* Only a pre-child preview: once the deposit invoice exists it shows its
          own (possibly frozen) amount below, so this recomputed line would just
          contradict it. */}
      {!isDepositDirty && computedDepositAmount > 0 && !invoice.depositChildId && (
        <p className="text-xs text-muted-foreground text-center pt-1">
          Deposit: <span className="font-semibold text-foreground">{formatCurrency(computedDepositAmount)}</span>
          {depositType === "percent" && ` (${depositValue}% of total)`}
        </p>
      )}

      {(() => {
        if (computedDepositAmount <= 0) return null;
        if (invoice.isDepositChild) return null;
        const isMilestoneParent = !!invoice.isMilestoneParent;
        const isMilestoneChild = !!invoice.milestoneParentId;
        const isRecurringParent = !!invoice.isRecurring && !invoice.recurringParentId;
        const isRecurringChild = !!invoice.recurringParentId;
        const blocked =
          isMilestoneParent ||
          isMilestoneChild ||
          isRecurringParent ||
          isRecurringChild;
        if (blocked) {
          let reason = "";
          if (isMilestoneParent || isMilestoneChild) reason = "Deposit invoices are not available when milestones are set up.";
          else if (isRecurringParent || isRecurringChild) reason = "Deposit invoices are not available on recurring invoices.";
          return (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 mt-2">
              {reason}
            </div>
          );
        }
        return (
          <>
            <DepositInvoiceAction
              invoice={invoice}
              invoiceId={invoiceId}
              depositAmount={computedDepositAmount}
              formatCurrency={formatCurrency}
            />
            <BalanceInvoiceStatus invoice={invoice} formatCurrency={formatCurrency} />
          </>
        );
      })()}
    </div>
  );

  // ── Milestone content ────────────────────────────────────────────────────────
  const progressContent = (
    <ProGate feature={GATE_MILESTONE} mode="overlay">
      <div className="space-y-3">
        {isChild ? (
          <div className="px-3 py-2.5 rounded-lg border border-violet-200 bg-violet-50 text-xs flex items-start gap-2">
            <Flag className="w-3.5 h-3.5 text-violet-600 shrink-0 mt-0.5" />
            <span className="text-violet-800">
              <span className="font-medium">Milestone invoice</span>
              {invoice.description ? <span className="text-violet-700"> — {invoice.description}</span> : null}
              <span className="block mt-0.5">
                <Link href={`/invoices/${invoice.milestoneParentId}`} className="font-medium underline hover:no-underline">
                  View parent invoice
                </Link>
              </span>
            </span>
          </div>
        ) : (
          <>
            {(!milestonesData || milestonesData.length === 0 || isMilestoneDirty) ? (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">Quick start</label>
                  <div className="flex flex-wrap gap-1.5">
                    {MILESTONE_TEMPLATES.map(t => (
                      <button
                        key={t.label}
                        type="button"
                        onClick={() => applyMilestoneTemplate(t)}
                        className="text-xs px-2.5 py-1.5 rounded-md border border-border bg-muted/40 hover:bg-muted hover:border-muted-foreground/30 transition-colors font-medium"
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  {milestoneRows.map((row, idx) => (
                    <div
                      key={row.id ?? `new-${idx}`}
                      className={cn("flex items-center gap-1.5 group", row.locked ? "opacity-60" : "")}
                      draggable={!row.locked}
                      onDragStart={() => handleMilestoneDragStart(idx)}
                      onDragOver={e => handleMilestoneDragOver(e, idx)}
                      onDragEnd={handleMilestoneDragEnd}
                    >
                      <span
                        className={cn(
                          "cursor-grab text-muted-foreground/50 hover:text-muted-foreground transition-colors shrink-0 select-none",
                          row.locked ? "cursor-default pointer-events-none" : ""
                        )}
                        title={row.locked ? "Cannot reorder invoiced milestone" : "Drag to reorder"}
                      >
                        <GripVertical className="w-3.5 h-3.5" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <input
                          type="text"
                          placeholder="Milestone name…"
                          value={row.name}
                          onChange={e => updateMilestoneRow(idx, "name", e.target.value)}
                          className="w-full text-xs h-7 rounded border border-border bg-background px-2 focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>
                      {!row.locked && (
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => updateMilestoneRow(idx, "usePercent", !row.usePercent)}
                            className={cn(
                              "text-xs px-1.5 py-0.5 rounded border font-medium transition-colors",
                              row.usePercent
                                ? "bg-primary/10 border-primary/30 text-primary"
                                : "bg-muted border-border text-muted-foreground"
                            )}
                            title={row.usePercent ? "Switch to fixed amount" : "Switch to percentage"}
                          >
                            {row.usePercent ? "%" : "$"}
                          </button>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max={row.usePercent ? "100" : undefined}
                            placeholder={row.usePercent ? "30" : "500"}
                            value={row.usePercent ? row.percentage : row.amount}
                            onChange={e => updateMilestoneRow(idx, row.usePercent ? "percentage" : "amount", e.target.value)}
                            className="w-16 text-xs h-7 rounded border border-border bg-background px-2 focus:outline-none focus:ring-1 focus:ring-primary text-right"
                          />
                        </div>
                      )}
                      {row.locked ? (
                        <span className="text-xs text-muted-foreground shrink-0 px-1">{row.usePercent ? `${row.percentage}%` : `$${row.amount}`}</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => removeMilestoneRow(idx)}
                          className="text-muted-foreground hover:text-destructive transition-colors p-0.5"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addMilestoneRow}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 mt-1"
                  >
                    <Plus className="w-3 h-3" /> Add milestone
                  </button>
                </div>

                {milestoneRows.some(r => r.usePercent) && (
                  <div className={cn(
                    "flex items-center justify-between text-xs px-2.5 py-1.5 rounded border",
                    milestoneIsComplete ? "bg-green-50 border-green-200 text-green-800" : milestoneIsValidPct ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-red-50 border-red-200 text-red-800"
                  )}>
                    <span>Total: {milestoneRoundedTotal.toFixed(1)}%</span>
                    {milestoneIsComplete ? (
                      <CheckCircle className="w-3.5 h-3.5" />
                    ) : milestoneIsValidPct ? (
                      <span>{(100 - milestoneRoundedTotal).toFixed(1)}% remaining</span>
                    ) : (
                      <AlertTriangle className="w-3.5 h-3.5" />
                    )}
                  </div>
                )}

                {milestoneRows.length > 0 && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleMilestoneSave} disabled={upsertMilestonesMutation.isPending || !milestoneIsValidPct}>
                      {upsertMilestonesMutation.isPending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                      Save Milestones
                    </Button>
                    {milestonesData && milestonesData.length > 0 && (
                      <Button size="sm" variant="ghost" onClick={() => { setIsMilestoneDirty(false); setMilestoneRows([]); }}>
                        Cancel
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {totalMilestones > 0 && (
                  <div className="rounded-md bg-muted/40 border px-3 py-2 space-y-1.5 text-xs">
                    <div className="flex items-center justify-between font-medium">
                      <span className="text-muted-foreground">{invoicedCount} of {totalMilestones} milestones invoiced</span>
                      <span className="text-foreground">{formatCurrencyLocal(billedAmount)} of {formatCurrencyLocal(invoice.total)} billed</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-1.5">
                      <div
                        className="bg-primary h-1.5 rounded-full transition-all"
                        style={{ width: `${invoice.total > 0 ? Math.min(100, (billedAmount / invoice.total) * 100) : 0}%` }}
                      />
                    </div>
                  </div>
                )}

                {isMilestonesLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 border rounded-md">
                        <div className="w-24 h-4 bg-muted animate-pulse rounded" />
                        <div className="w-16 h-4 bg-muted animate-pulse rounded" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded border divide-y text-xs">
                    {milestonesData?.map((milestone: MilestoneItem) => {
                      const milestoneAmt = milestone.amount !== null && milestone.amount !== undefined
                        ? milestone.amount
                        : milestone.percentage !== null && milestone.percentage !== undefined
                          ? Math.round(invoice.total * (milestone.percentage / 100) * 100) / 100
                          : 0;
                      // When an earlier milestone is frozen (sent/paid) at a different
                      // amount than it would bill today, the remaining unsent milestones
                      // absorb the difference proportionally (see reflowDraftMilestones on
                      // the server) — so this milestone's actual dollar amount can drift
                      // from a literal percentage of the current total.
                      const statedPctAmount = milestone.percentage !== null && milestone.percentage !== undefined
                        ? Math.round(invoice.total * (milestone.percentage / 100) * 100) / 100
                        : null;
                      const amountAdjusted = statedPctAmount !== null && Math.abs(statedPctAmount - milestoneAmt) >= 0.01;
                      return (
                        <div key={milestone.id} className="px-3 py-2 space-y-1.5 hover:bg-muted/20 transition-colors">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5 min-w-0">
                              {getMilestoneStatusIcon(milestone.status)}
                              <span className="font-medium truncate">{milestone.name}</span>
                              <span className={cn("shrink-0 px-1.5 py-0.5 rounded border font-medium capitalize", getMilestoneStatusColor(milestone.status))}>
                                {milestone.status}
                              </span>
                            </div>
                            <span className="font-semibold shrink-0">
                              {milestone.percentage !== null && milestone.percentage !== undefined
                                ? `${milestone.percentage}%`
                                : formatCurrencyLocal(milestone.amount ?? 0)
                              }
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">{formatCurrencyLocal(milestoneAmt)}</span>
                            <div className="flex items-center gap-1.5">
                              {amountAdjusted && (
                                <span
                                  className="text-[10px] text-amber-600 dark:text-amber-500"
                                  title={`Adjusted from ${formatCurrencyLocal(statedPctAmount ?? 0)} (${milestone.percentage}% of the current total) to keep all milestones summing to the full invoice`}
                                >
                                  Adjusted
                                </span>
                              )}
                              {milestone.status === "pending" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 text-[11px] px-2 gap-1"
                                  data-walkthrough-target="milestone-complete-btn"
                                  disabled={completingId === milestone.id}
                                  onClick={() => handleComplete(milestone)}
                                >
                                  {completingId === milestone.id ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <CheckCircle className="w-3 h-3" />
                                  )}
                                  Invoice
                                </Button>
                              )}
                              {milestone.childInvoiceId != null && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 text-[11px] px-2 gap-1"
                                  onClick={() => setLocation(`/invoices/${milestone.childInvoiceId}`)}
                                >
                                  Open
                                  <ArrowRight className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <Button
                  size="sm"
                  variant="ghost"
                  className="text-xs"
                  onClick={() => {
                    setIsMilestoneDirty(true);
                    if (milestonesData) {
                      setMilestoneRows(milestonesData.map((m: MilestoneItem) => ({
                        id: m.id,
                        name: m.name,
                        percentage: m.percentage !== null && m.percentage !== undefined ? String(m.percentage) : "",
                        amount: m.amount !== null && m.amount !== undefined ? String(m.amount) : "",
                        usePercent: m.percentage !== null && m.percentage !== undefined,
                        locked: m.status !== "pending",
                      })));
                    }
                  }}
                >
                  <Edit className="w-3 h-3 mr-1" /> Edit milestones
                </Button>

                <p className="text-xs text-muted-foreground pt-1 border-t">
                  This parent invoice is not sent to the customer. Generated milestone invoices are what get shared.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </ProGate>
  );

  const milestoneBadge = (invoiceMilestones?.length ?? 0) > 0 ? (
    <span className="ml-0.5 text-xs bg-violet-100 whitespace-nowrap text-violet-700 border border-violet-200 px-1 py-0.5 rounded font-medium">
      {invoicedCount} of {invoiceMilestones.length}
    </span>
  ) : undefined;

  return (
    <PaymentPlanPanel
      mode={mode}
      onModeChange={handleModeChange}
      isModeChangePending={isModeChangePending}
      depositContent={depositContent}
      progressContent={progressContent}
      milestoneBadge={milestoneBadge}
      isProgressDisabled={false}
      isProgressGated={!isPro}
      isRecurringActive={isRecurringActive}
    />
  );
}

const RECURRING_INTERVAL_OPTIONS = [
  { value: "weekly", label: "Weekly", defaultCount: 52 },
  { value: "biweekly", label: "Bi-weekly", defaultCount: 26 },
  { value: "monthly", label: "Monthly", defaultCount: 12 },
  { value: "quarterly", label: "Quarterly", defaultCount: 4 },
  { value: "semiannually", label: "Semi-annually", defaultCount: 2 },
  { value: "annually", label: "Annually", defaultCount: 1 },
  { value: "custom", label: "Custom", defaultCount: 12 },
] as const;

function normalizeInterval(iv: string | null | undefined): string {
  if (!iv) return "monthly";
  if (iv === "first_of_month" || iv === "fifteenth_of_month" || iv === "last_of_month" || iv === "custom_months") return "monthly";
  if (iv === "custom_days") return "custom";
  return iv;
}

function RecurringPanel({ invoice, invoiceId, isPaymentPlanActive = false }: { invoice: InvoiceDetail; invoiceId: number; isPaymentPlanActive?: boolean }) {
  const queryClient = useQueryClient();
  const updateInvoice = useUpdateInvoice();
  const approveRecurringMutation = useApproveRecurring();
  const { toast } = useToast();

  const [isOpen, setIsOpen] = useState(true);
  const [interval, setIntervalValue] = useState(normalizeInterval(invoice.recurringInterval));
  const [startDate, setStartDate] = useState(invoice.recurringStartDate ?? "");
  const [endDate, setEndDate] = useState(invoice.recurringEndDate ?? "");
  const [autoSend, setAutoSend] = useState(invoice.recurringAutoSend ?? false);
  const [startDateError, setStartDateError] = useState(false);
  const [activateChoiceOpen, setActivateChoiceOpen] = useState(false);

  const isMilestoneChild = !!invoice.milestoneParentId;

  // The first copy date (recurringNextDate) is computed from the start date at
  // save time. If it's already on/before today, activating now would otherwise
  // skip those elapsed periods — so we offer a back-bill choice.
  const todayStr = new Date().toISOString().slice(0, 10);
  const hasMissedPeriods = !!invoice.recurringNextDate && invoice.recurringNextDate <= todayStr;

  const runActivation = (backfill: boolean) => {
    approveRecurringMutation.mutate({ invoiceId, backfill }, {
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
        queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
        setActivateChoiceOpen(false);
        const backfilled = result.backfilledCount ?? 0;
        toast({
          title: "Recurring activated",
          description: backfilled > 0
            ? `Created ${backfilled} draft${backfilled === 1 ? "" : "s"} for past periods. Next copy due ${result.recurringNextDate ?? ""}`.trim()
            : (result.recurringNextDate ? `First copy due ${result.recurringNextDate}` : undefined),
        });
      },
      onError: () => toast({ title: "Failed to activate recurring", variant: "destructive" }),
    });
  };

  const deriveMode = (): "off" | "recurring" => {
    if (invoice.isRecurring) return "recurring";
    return "off";
  };
  const [mode, setMode] = useState<"off" | "recurring">(deriveMode());

  useEffect(() => {
    setMode(deriveMode());
    setIntervalValue(normalizeInterval(invoice.recurringInterval));
    setStartDate(invoice.recurringStartDate ?? "");
    setEndDate(invoice.recurringEndDate ?? "");
    setAutoSend(invoice.recurringAutoSend ?? false);
  }, [invoice.isRecurring, invoice.recurringInterval, invoice.recurringStartDate, invoice.recurringEndDate, invoice.recurringAutoSend]);

  const mutateInvoice = (data: Parameters<typeof updateInvoice.mutate>[0]["data"]) => {
    updateInvoice.mutate({ id: invoiceId, data }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
        queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
      },
      onError: () => toast({ title: "Failed to save recurring settings", variant: "destructive" }),
    });
  };

  const save = (overrides?: Record<string, unknown>) => {
    const isRecurring = mode !== "off";
    const effectiveStartDate = (overrides?.recurringStartDate as string | null | undefined) !== undefined
      ? (overrides?.recurringStartDate as string | null)
      : (startDate || null);
    if (isRecurring && !effectiveStartDate) {
      setStartDateError(true);
      return;
    }
    setStartDateError(false);
    const base = {
      isRecurring,
      recurringInterval: interval,
      recurringCustomDays: null,
      recurringStartDate: startDate || null,
      recurringEndDate: endDate || null,
      recurringAutoSend: autoSend,
    };
    mutateInvoice({ ...base, ...overrides } as Parameters<typeof updateInvoice.mutate>[0]["data"]);
  };

  const handleModeChange = (newMode: "off" | "recurring") => {
    if (newMode !== "off" && isPaymentPlanActive) return;
    setMode(newMode);
    if (newMode !== "off" && !startDate) {
      setStartDateError(true);
      return;
    }
    setStartDateError(false);
    mutateInvoice({
      isRecurring: newMode !== "off",
      recurringInterval: interval,
      recurringCustomDays: null,
      recurringStartDate: startDate || null,
      recurringEndDate: endDate || null,
      recurringAutoSend: autoSend,
    } as Parameters<typeof updateInvoice.mutate>[0]["data"]);
  };

  const intervalLabel = RECURRING_INTERVAL_OPTIONS.find(o => o.value === interval)?.label ?? "Monthly";

  const MODES = [
    { value: "off" as const, label: "Off" },
    { value: "recurring" as const, label: "Recurring" },
  ];

  return (
    <>
      {isMilestoneChild ? (
        <div className="px-4 py-3 rounded-lg border border-violet-200 bg-violet-50 text-sm flex items-center gap-2">
          <Layers className="w-4 h-4 text-violet-600 shrink-0" />
          <span className="text-violet-800">
            This is a milestone invoice. Recurring is not available.{" "}
            <Link href={`/invoices/${invoice.milestoneParentId}`} className="font-medium underline hover:no-underline">
              View parent invoice
            </Link>
          </span>
        </div>
      ) : null}
      {isMilestoneChild ? null : <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <Card className="shadow-sm border-border/50">
          <CardHeader className="border-b p-0 bg-muted/40">
            <CollapsibleTrigger asChild>
              <button type="button" className="w-full flex items-center justify-between px-6 py-4 hover:bg-muted/30 transition-colors rounded-t-lg">
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-semibold">Recurring</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") e.stopPropagation(); }}
                        className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                        aria-label="Learn about recurring invoices"
                      >
                        <Info className="w-3.5 h-3.5" />
                      </span>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 text-sm" align="start">
                      <p className="font-medium mb-1">How recurring invoices work</p>
                      <p className="text-muted-foreground">
                        When recurring is enabled, this invoice acts as a <strong>parent template</strong>. QuoTrak automatically generates a new draft copy on each billing date — you review and send each one at your convenience.
                      </p>
                    </PopoverContent>
                  </Popover>
                  {mode === "recurring" && (
                    <span className="text-xs bg-blue-100 text-blue-700 border border-blue-200 px-1.5 py-0.5 rounded font-medium">
                      {intervalLabel}
                    </span>
                  )}
                </div>
                <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform duration-200", isOpen && "rotate-180")} />
              </button>
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="pt-4 space-y-3">
              <div className="flex gap-1 p-1 bg-border rounded-lg text-sm font-medium">
                {MODES.map((m) => {
                  const isDisabledPaymentPlan = m.value !== "off" && isPaymentPlanActive;
                  const isDisabled = isDisabledPaymentPlan;
                  const isActive = mode === m.value;
                  const tooltipText = isDisabledPaymentPlan
                    ? "Turn off the Payment Plan first to use Recurring."
                    : null;
                  const btn = (
                    <button
                      key={m.value}
                      type="button"
                      disabled={isDisabled}
                      onClick={() => handleModeChange(m.value)}
                      className={cn(
                        "flex-1 px-3 py-1.5 rounded-md transition-colors",
                        isActive ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground",
                        isDisabled && "opacity-40 cursor-not-allowed"
                      )}
                    >
                      {m.label}
                    </button>
                  );
                  if (tooltipText) {
                    return (
                      <Tooltip key={m.value}>
                        <TooltipTrigger asChild>
                          <span className="flex-1">{btn}</span>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-[220px] text-xs">
                          {tooltipText}
                        </TooltipContent>
                      </Tooltip>
                    );
                  }
                  return btn;
                })}
              </div>

              {mode !== "off" && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-xs text-muted-foreground font-medium">Billing cycle</label>
                    <Select value={interval} onValueChange={(v) => { setIntervalValue(v); save({ recurringInterval: v, recurringCustomDays: null }); }}>
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {RECURRING_INTERVAL_OPTIONS.map(o => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs text-muted-foreground font-medium">
                      Start date
                      <span className="text-destructive ml-0.5">*</span>
                    </label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={e => {
                        setStartDate(e.target.value);
                        if (e.target.value) setStartDateError(false);
                      }}
                      onBlur={() => save({ recurringStartDate: startDate || null })}
                      className={cn("h-8 text-sm", startDateError && "border-destructive focus-visible:ring-destructive")}
                    />
                    {startDateError && (
                      <p className="text-xs text-destructive">Start date is required</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs text-muted-foreground font-medium">End date (optional)</label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                      onBlur={() => save({ recurringEndDate: endDate || null })}
                      className="h-8 text-sm"
                    />
                  </div>

                  <div className="flex items-center justify-between pt-1 border-t">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">Auto-send when generated</span>
                        <span className="text-xs text-muted-foreground">Copies are marked sent and emailed to the customer</span>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={autoSend}
                        onClick={() => {
                          const next = !autoSend;
                          setAutoSend(next);
                          save({ recurringAutoSend: next });
                        }}
                        className={cn(
                          "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors",
                          autoSend ? "bg-primary" : "bg-muted"
                        )}
                      >
                        <span className={cn(
                          "pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-md ring-0 transition-transform",
                          autoSend ? "translate-x-5" : "translate-x-0"
                        )} />
                      </button>
                    </div>

                  {invoice.recurringNextDate && (
                    <p className="text-xs text-muted-foreground pt-1 border-t">
                      Next copy: <span className="font-semibold text-foreground">{invoice.recurringNextDate}</span>
                    </p>
                  )}

                  {!invoice.recurringApprovedAt ? (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-3 space-y-2 mt-1">
                      <p className="text-xs text-amber-800 dark:text-amber-200 font-medium">Recurring is not active yet</p>
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        Activate to let QuoTrak start generating copies on schedule. The first copy will be due on <span className="font-semibold">{invoice.recurringNextDate ?? "the configured start date"}</span>.
                      </p>
                      <button
                        type="button"
                        disabled={approveRecurringMutation.isPending || !startDate}
                        onClick={() => {
                          if (hasMissedPeriods) {
                            setActivateChoiceOpen(true);
                          } else {
                            runActivation(false);
                          }
                        }}
                        className={cn(
                          "w-full h-8 text-xs font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 transition-colors",
                          (approveRecurringMutation.isPending || !startDate) && "opacity-50 cursor-not-allowed"
                        )}
                      >
                        {approveRecurringMutation.isPending ? "Activating…" : "Activate recurring"}
                      </button>

                      <AlertDialog open={activateChoiceOpen} onOpenChange={setActivateChoiceOpen}>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Back-bill past periods?</AlertDialogTitle>
                            <AlertDialogDescription>
                              The start date is in the past, so one or more billing periods have already elapsed. Choose how to activate. Back-billed invoices are created as <span className="font-medium">drafts</span> — they are never auto-sent or auto-charged.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter className="flex-col sm:flex-col gap-2">
                            <button
                              type="button"
                              disabled={approveRecurringMutation.isPending}
                              onClick={() => runActivation(true)}
                              className={cn(
                                "w-full h-9 text-sm font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 transition-colors",
                                approveRecurringMutation.isPending && "opacity-50 cursor-not-allowed"
                              )}
                            >
                              Back-bill missed periods as drafts
                            </button>
                            <button
                              type="button"
                              disabled={approveRecurringMutation.isPending}
                              onClick={() => runActivation(false)}
                              className={cn(
                                "w-full h-9 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent transition-colors",
                                approveRecurringMutation.isPending && "opacity-50 cursor-not-allowed"
                              )}
                            >
                              Start from the next upcoming period
                            </button>
                            <AlertDialogCancel className="w-full mt-0" disabled={approveRecurringMutation.isPending}>Cancel</AlertDialogCancel>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      {!startDate && (
                        <p className="text-xs text-amber-700 dark:text-amber-300">Set a start date above before activating.</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-green-700 dark:text-green-400 pt-1 border-t flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                      Active — copies generated automatically on schedule
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>}
    </>
  );
}


// ─── Milestone Panel ─────────────────────────────────────────────────────────

const MILESTONE_TEMPLATES = [
  {
    label: "30 / 40 / 30",
    milestones: [
      { name: "Deposit", percentage: 30 },
      { name: "Progress payment", percentage: 40 },
      { name: "Final payment", percentage: 30 },
    ],
  },
  {
    label: "25 × 4",
    milestones: [
      { name: "Deposit", percentage: 25 },
      { name: "Milestone 2", percentage: 25 },
      { name: "Milestone 3", percentage: 25 },
      { name: "Final payment", percentage: 25 },
    ],
  },
  {
    label: "Deposit + 2 progress + final",
    milestones: [
      { name: "Deposit", percentage: 20 },
      { name: "Rough-in complete", percentage: 35 },
      { name: "Finishing work", percentage: 30 },
      { name: "Final inspection", percentage: 15 },
    ],
  },
] as const;

type MilestoneRow = { id?: number; name: string; percentage: string; amount: string; usePercent: boolean; locked?: boolean };

function getMilestoneStatusIcon(status: string) {
  switch (status) {
    case "paid": return <CheckCircle className="w-3.5 h-3.5 text-green-600" />;
    case "invoiced": return <FileText className="w-3.5 h-3.5 text-blue-600" />;
    default: return <Clock className="w-3.5 h-3.5 text-muted-foreground" />;
  }
}

function getMilestoneStatusColor(status: string) {
  switch (status) {
    case "paid": return "bg-green-100 text-green-800 border-green-200";
    case "invoiced": return "bg-blue-100 text-blue-800 border-blue-200";
    default: return "bg-gray-100 text-gray-800 border-gray-200";
  }
}

// ─── Overdue Reminders Panel ─────────────────────────────────────────────────

function OverdueRemindersPanel({ invoice, invoiceId }: { invoice: InvoiceDetail; invoiceId: number }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const updateInvoice = useUpdateInvoice();

  const enabled = invoice.overdueReminderEnabled ?? false;
  const days = invoice.overdueReminderDays ?? 7;

  const [localDays, setLocalDays] = useState(String(days));

  const setEnabled = (val: boolean) => {
    const currentDays = invoice.overdueReminderDays ?? null;
    const patchData: { overdueReminderEnabled: boolean; overdueReminderDays?: number } = { overdueReminderEnabled: val };
    if (val && !currentDays) {
      patchData.overdueReminderDays = 7;
      setLocalDays("7");
    }
    updateInvoice.mutate(
      { id: invoiceId, data: patchData },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
        },
        onError: () => toast({ title: "Failed to update reminder settings", variant: "destructive" }),
      }
    );
  };

  const saveDays = () => {
    const parsed = parseInt(localDays, 10);
    if (isNaN(parsed) || parsed < 1) {
      toast({ title: "Please enter a valid number of days (minimum 1)", variant: "destructive" });
      return;
    }
    updateInvoice.mutate(
      { id: invoiceId, data: { overdueReminderDays: parsed } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
          toast({ title: "Reminder interval saved" });
        },
        onError: () => toast({ title: "Failed to save reminder interval", variant: "destructive" }),
      }
    );
  };

  return (
    <Card className="shadow-sm border-border/50">
      <CardHeader className="pb-3 border-b bg-muted/40">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            <CardTitle className="text-sm font-semibold">Overdue Reminders</CardTitle>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            onClick={() => setEnabled(!enabled)}
            className={cn(
              "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors",
              enabled ? "bg-primary" : "bg-muted"
            )}
          >
            <span className={cn(
              "pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-md ring-0 transition-transform",
              enabled ? "translate-x-5" : "translate-x-0"
            )} />
          </button>
        </div>
        <CardDescription className="text-xs">
          Automatically email the customer when this invoice is overdue.
        </CardDescription>
      </CardHeader>
      {enabled && (
        <CardContent className="pt-4 space-y-3">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Send reminder every X days after due date</label>
            <div className="flex gap-2">
              <Input
                type="number"
                min={1}
                className="h-8 text-sm"
                value={localDays}
                onChange={e => setLocalDays(e.target.value)}
                onBlur={saveDays}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); saveDays(); } }}
              />
              <span className="flex items-center text-sm text-muted-foreground shrink-0">days</span>
            </div>
            <p className="text-xs text-muted-foreground">
              A reminder email will be sent to the customer every {parseInt(localDays, 10) || 7} day(s) past the due date until paid or voided.
            </p>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// ─── Payment Link Panel ───────────────────────────────────────────────────────

function PaymentLinkPanel({ invoice, invoiceId }: { invoice: InvoiceDetail; invoiceId: number }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  const updateInvoice = useUpdateInvoice();

  const acceptOnlinePayment = invoice.acceptOnlinePayment ?? true;

  const setAcceptOnlinePayment = (val: boolean) => {
    updateInvoice.mutate(
      { id: invoiceId, data: { acceptOnlinePayment: val } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
        },
        onError: () => toast({ title: "Failed to update payment settings", variant: "destructive" }),
      }
    );
  };

  const { data: connectStatus } = useGetConnectStatus({
    query: { queryKey: [...CONNECT_STATUS_QUERY_KEY] }
  });

  const generateLink = useGenerateInvoicePaymentLink({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
        toast({ title: "Payment link generated" });
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { error?: string } }; message?: string };
        const message = e?.response?.data?.error || e?.message || "Failed to generate payment link";
        toast({ title: message, variant: "destructive" });
      },
    },
  });

  const canGenerateLink = invoice.status === "sent" || invoice.status === "overdue";

  const copyLink = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const connectState = connectStatus?.status ?? null;
  const isActive = connectStatus?.connected && connectState === "active";
  const isPending = connectStatus?.connected && connectState === "pending";
  const isDisabled = connectStatus?.connected && connectState === "disabled";

  return (
    <Card className="shadow-sm border-border/50">
      <CardHeader className="pb-3 border-b bg-muted/40">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-primary" />
            <CardTitle className="text-sm font-semibold">Online Payment</CardTitle>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={acceptOnlinePayment}
            onClick={() => setAcceptOnlinePayment(!acceptOnlinePayment)}
            className={cn(
              "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors",
              acceptOnlinePayment ? "bg-primary" : "bg-muted"
            )}
          >
            <span className={cn(
              "pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-md ring-0 transition-transform",
              acceptOnlinePayment ? "translate-x-5" : "translate-x-0"
            )} />
          </button>
        </div>
        <CardDescription className="text-xs">
          {acceptOnlinePayment
            ? "Payment link will be included in emails and PDF for this invoice."
            : "Online payment is disabled — no payment link will be shown to the customer."}
        </CardDescription>
        {acceptOnlinePayment && invoice.recurringParentId && (
          <div className="flex items-start gap-2 rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-700 mt-2">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>Your client will have the option to save their card for automatic payments on future invoices in this series.</span>
          </div>
        )}
        {acceptOnlinePayment && invoice.isRecurring && !invoice.recurringParentId && (
          invoice.recurringAutoSend ? (
            <div className="flex items-start gap-2 rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-700 mt-2">
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>Your client will have the option to save their card for automatic payments on future invoices in this series.</span>
            </div>
          ) : (
            <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700 mt-2">
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>Enable auto-send when generated to prompt your customer to enable auto-pay.</span>
            </div>
          )
        )}
      </CardHeader>
      {acceptOnlinePayment && (
      <CardContent className="pt-4 space-y-3">
        {!canGenerateLink && !invoice.stripePaymentLinkUrl ? (
          <p className="text-xs text-muted-foreground">
            Mark this invoice as sent, or send this invoice to a customer first to generate a payment link.
          </p>
        ) : invoice.stripePaymentLinkUrl ? (
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded-md px-2.5 py-1.5">
              <CheckCircle className="w-3.5 h-3.5 shrink-0" />
              Payment link ready to share
            </div>
            <div className="flex gap-2">
              <Input
                readOnly
                value={invoice.stripePaymentLinkUrl}
                className="text-xs h-8 bg-muted/50 font-mono"
              />
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-8 w-8 shrink-0"
                    onClick={() => copyLink(invoice.stripePaymentLinkUrl!)}
                  >
                    {copied ? <CheckCircle className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{copied ? "Copied!" : "Copy link"}</TooltipContent>
              </Tooltip>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-8 w-8 shrink-0"
                    onClick={() => window.open(invoice.stripePaymentLinkUrl!, "_blank")}
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Open link</TooltipContent>
              </Tooltip>
            </div>
          </div>
        ) : isActive ? (
          <Button
            size="sm"
            onClick={() => generateLink.mutate(invoiceId)}
            disabled={generateLink.isPending}
            className="w-full"
          >
            {generateLink.isPending ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Loading…</>
            ) : (
              <><Link2 className="w-3.5 h-3.5 mr-1.5" /> View / Copy Payment Link</>
            )}
          </Button>
        ) : isDisabled ? (
          <div className="space-y-2">
            <p className="text-xs text-destructive">
              Your Stripe account has been disabled. Resolve outstanding requirements in Settings.
            </p>
            <Button size="sm" variant="outline" className="w-full" asChild>
              <Link href="/settings">
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                Go to Settings
              </Link>
            </Button>
          </div>
        ) : isPending ? (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Complete your Stripe onboarding in Settings to generate payment links.
            </p>
            <Button size="sm" variant="outline" className="w-full" asChild>
              <Link href="/settings">
                <Zap className="w-3.5 h-3.5 mr-1.5" />
                Continue Onboarding
              </Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Connect your Stripe account in Settings to generate payment links.
            </p>
            <Button size="sm" variant="outline" className="w-full" asChild>
              <Link href="/settings">
                <Zap className="w-3.5 h-3.5 mr-1.5" />
                Connect Stripe in Settings
              </Link>
            </Button>
          </div>
        )}
      </CardContent>
      )}
    </Card>
  );
}

// ─── Auto-Charge Failed Banner ────────────────────────────────────────────────

function AutoChargeFailedBanner({ failedAt }: { failedAt: string }) {
  const [dismissed, setDismissed] = useState(false);
  const { formatDate } = useSettingsFormat();

  if (dismissed) return null;

  const failedDate = formatDate ? formatDate(failedAt) : new Date(failedAt).toLocaleDateString();

  return (
    <div className="flex items-start gap-3 px-4 py-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm">
      <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
      <p className="flex-1">
        <span className="font-medium">Auto-charge failed on {failedDate}</span> — a payment link was sent to the customer instead.
      </p>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="p-0.5 text-amber-500 hover:text-amber-700 shrink-0 transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── Invoice Detail Page ──────────────────────────────────────────────────────

export default function InvoiceDetail() {
  const params = useParams();
  const invoiceId = parseInt(params.id || "0", 10);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { hasProAccess: isPro } = getTrialInfo(user);

  const [isDeleteDialogOpen, setIsDeleteOpen] = useState(false);
  const [localPaymentPlanMode, setLocalPaymentPlanMode] = useState<PaymentMode>("none");
  const [markSentForPdfOpen, setMarkSentForPdfOpen] = useState(false);
  const [pendingPdfAction, setPendingPdfAction] = useState<"preview" | "export" | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [ccSelf, setCcSelf] = useState(true);
  const [sendReceiptOpen, setSendReceiptOpen] = useState(false);
  const [ccSelfReceipt, setCcSelfReceipt] = useState(false);
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [paymentNotes, setPaymentNotes] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [sendReceiptPaymentId, setSendReceiptPaymentId] = useState<number | null>(null);
  const [deletePaymentId, setDeletePaymentId] = useState<number | null>(null);
  const [receiptPreviewOpen, setReceiptPreviewOpen] = useState(false);
  const [receiptPreviewBlobUrl, setReceiptPreviewBlobUrl] = useState<string | null>(null);
  const [receiptPreviewFileName, setReceiptPreviewFileName] = useState("receipt.pdf");
  const [receiptPreviewPaymentId, setReceiptPreviewPaymentId] = useState<number | null>(null);
  const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);
  const [saveAsTemplateName, setSaveAsTemplateName] = useState("");
  const [saveAsTemplateDesc, setSaveAsTemplateDesc] = useState("");
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [addLineItemOpen, setAddLineItemOpen] = useState(false);
  const [addingToGroupId, setAddingToGroupId] = useState<number | null>(null);

  const { data: invoice, isLoading } = useGetInvoice(invoiceId, {
    query: { enabled: !!invoiceId, queryKey: getGetInvoiceQueryKey(invoiceId) },
  });
  usePageTitle(invoice?.title ?? "Invoice");

  const { data: companySettings } = useGetSettings({
    query: { queryKey: getGetSettingsQueryKey() },
  });

  const { data: invoiceMilestones } = useGetMilestones(invoiceId, {
    query: { queryKey: getListMilestonesQueryKey(invoiceId), enabled: !!invoiceId },
  });

  const updateInvoice = useUpdateInvoice();
  const deleteInvoice = useDeleteInvoice();
  const duplicateInvoice = useDuplicateInvoice();
  const sendEmailMutation = useSendInvoiceEmail();
  const sendReminderMutation = useSendInvoiceReminder();
  const sendReceiptMutation = useSendInvoiceReceipt();
  const recordPaymentMutation = useRecordInvoicePayment();
  const createInvoiceGroup = useCreateInvoiceLineItemGroup();

  const handleAddInvoiceGroup = () => {
    if (!invoiceId) return;
    const groups = invoice?.groups ?? [];
    const maxSort = groups.reduce((m, g) => Math.max(m, g.sortOrder ?? 0), -1);
    const accentColor = GROUP_ACCENT_COLORS[groups.length % GROUP_ACCENT_COLORS.length].hex;
    createInvoiceGroup.mutate(
      { invoiceId, data: { name: "New Group", sortOrder: maxSort + 1, accentColor } },
      {
        onSuccess: () => queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) }),
        onError: () => toast({ title: "Failed to add group", variant: "destructive" }),
      }
    );
  };

  // QuickBooks integration is hidden behind "Coming Soon" — task #762.
  // The push-to-QuickBooks menu item is no longer rendered. Hooks
  // (useGetQBOStatus, usePushInvoiceToQBO, useGetQBOInvoiceSyncStatus) are
  // intentionally not called here — the underlying API client functions
  // stay in @workspace/api-client-react for when the integration is
  // unhidden.
  const deletePaymentMutation = useDeleteInvoicePayment();
  const sendPaymentReceiptMutation = useSendPaymentReceipt();
  const { data: invoicePayments = [] } = useListInvoicePayments(invoiceId, {
    query: { queryKey: getListInvoicePaymentsQueryKey(invoiceId), enabled: !!invoiceId },
  });
  const createCustomer = useCreateCustomer();

  const handleSendReminder = () => {
    sendReminderMutation.mutate({ id: invoiceId }, {
      onSuccess: (result) => {
        toast({ title: "Overdue reminder sent", description: `Sent to ${result.to}` });
        queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
        queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { error?: string } }; message?: string };
        const message = e?.response?.data?.error || e?.message || "Failed to send reminder";
        toast({ title: message, variant: "destructive" });
      },
    });
  };

  const { data: customers, isLoading: isLoadingCustomers } = useListCustomers();

  const [createCustomerModalOpen, setCreateCustomerModalOpen] = useState(false);
  const [pendingCustomerName, setPendingCustomerName] = useState("");

  const [inlineCustomerOpen, setInlineCustomerOpen] = useState(false);
  const [inlineCustomerSearch, setInlineCustomerSearch] = useState("");

  const saveInvoiceField = (field: Partial<z.infer<typeof updateInvoiceSchema>>) => {
    updateInvoice.mutate({ id: invoiceId, data: field }, {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) }),
      onError: () => toast({ title: "Failed to save", variant: "destructive" }),
    });
  };

  const handleCreateCustomerFromInline = (data: CreateCustomerFormValues) => {
    createCustomer.mutate(
      { data: { name: data.name, email: data.email || undefined, phone: data.phone || undefined, addressLine1: data.addressLine1 || undefined } },
      {
        onSuccess: (newCustomer) => {
          queryClient.invalidateQueries({ queryKey: getListCustomersQueryKey() });
          setCreateCustomerModalOpen(false);
          setInlineCustomerOpen(false);
          setInlineCustomerSearch("");
          updateInvoice.mutate({ id: invoiceId, data: { customerId: newCustomer.id } }, {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
              toast({ title: "Customer updated" });
            },
            onError: () => {
              toast({ title: "Failed to assign customer to invoice", variant: "destructive" });
            },
          });
        },
        onError: () => {
          toast({ title: "Failed to create customer", variant: "destructive" });
        },
      }
    );
  };

  const handleInlineCustomerChange = (customerId: number) => {
    if (customerId === invoice?.customerId) {
      setInlineCustomerOpen(false);
      setInlineCustomerSearch("");
      return;
    }
    updateInvoice.mutate({ id: invoiceId, data: { customerId } }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
        setInlineCustomerOpen(false);
        setInlineCustomerSearch("");
        toast({ title: "Customer updated" });
      },
      onError: () => {
        toast({ title: "Failed to update customer", variant: "destructive" });
      },
    });
  };

  const openRecordPayment = () => {
    const total = invoice?.total ?? 0;
    const alreadyPaid = invoicePayments.reduce((s: number, p: InvoicePaymentItem) => s + p.amount, 0);
    const balance = Math.max(0, total - alreadyPaid);
    setPaymentAmount(balance > 0 ? balance.toFixed(2) : total.toFixed(2));
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setPaymentNotes("");
    setPaymentMethod("");
    setRecordPaymentOpen(true);
  };

  const onStatusChange = (status: "draft" | "sent" | "paid" | "overdue" | "void") => {
    if (status === "paid") {
      openRecordPayment();
      return;
    }
    updateInvoice.mutate({ id: invoiceId, data: { status } }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
        queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
        toast({ title: `Status updated to ${status}` });
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { error?: string } }; message?: string };
        const message = e?.response?.data?.error || e?.message || "Failed to update status";
        toast({ title: message, variant: "destructive" });
      },
    });
  };

  const onDelete = () => {
    deleteInvoice.mutate({ id: invoiceId }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
        toast({ title: "Invoice deleted" });
        setLocation("/invoices");
      },
    });
  };

  const onDuplicate = () => {
    duplicateInvoice.mutate({ id: invoiceId }, {
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
        toast({ title: "Invoice duplicated" });
        setLocation(`/invoices/${result.id}`);
      },
      onError: () => toast({ title: "Failed to duplicate invoice", variant: "destructive" }),
    });
  };

  const { formatCurrency, formatDate } = useSettingsFormat();

  const getInvoiceForPdf = (overrideInvoice?: typeof invoice) => ({
    ...(overrideInvoice ?? invoice)!,
    createdAt: String((overrideInvoice ?? invoice)!.createdAt),
    milestones: invoiceMilestones ?? [],
    payments: invoicePayments,
  });

  const shouldPromptMarkSent = !!(
    invoice &&
    invoice.status === "draft" &&
    invoice.acceptOnlinePayment !== false &&
    !invoice.stripePaymentLinkUrl
  );

  const handlePdfAction = (action: "preview" | "export") => {
    if (shouldPromptMarkSent) {
      setPendingPdfAction(action);
      setMarkSentForPdfOpen(true);
    } else {
      if (action === "preview") {
        void previewInvoicePDF(getInvoiceForPdf(), companySettings ?? {}).then((url) => {
          setPreviewBlobUrl(url);
          setPreviewTitle("Invoice PDF");
          setPreviewOpen(true);
        });
      } else {
        void exportInvoicePDF(getInvoiceForPdf(), companySettings ?? {});
      }
    }
  };

  const handleMarkSentAndExport = async () => {
    setMarkSentForPdfOpen(false);
    try {
      const result = await updateInvoice.mutateAsync({ id: invoiceId, data: { status: "sent" } });
      queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
      queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
      const pdfData = getInvoiceForPdf({ ...invoice!, ...result } as typeof invoice);
      if (pendingPdfAction === "preview") {
        const url = await previewInvoicePDF(pdfData, companySettings ?? {});
        setPreviewBlobUrl(url);
        setPreviewTitle("Invoice PDF");
        setPreviewOpen(true);
      } else {
        void exportInvoicePDF(pdfData, companySettings ?? {});
      }
    } catch {
      toast({ title: "Failed to mark as sent", variant: "destructive" });
    } finally {
      setPendingPdfAction(null);
    }
  };

  const handleExportAsDraft = () => {
    setMarkSentForPdfOpen(false);
    setPendingPdfAction(null);
    if (pendingPdfAction === "preview") {
      void previewInvoicePDF(getInvoiceForPdf(), companySettings ?? {}).then((url) => {
        setPreviewBlobUrl(url);
        setPreviewTitle("Invoice PDF");
        setPreviewOpen(true);
      });
    } else {
      void exportInvoicePDF(getInvoiceForPdf(), companySettings ?? {});
    }
  };

  const getReceiptInvoiceData = (): ReceiptInvoiceData => ({
    invoiceNumber: invoice!.invoiceNumber,
    customerName: invoice!.customerName,
    customerPhone: invoice!.customerPhone,
    customerEmail: invoice!.customerEmail,
    total: invoice!.total,
  });

  const openReceiptPreview = (paymentId: number | null) => {
    if (!invoice) return;
    const payments = invoicePayments;
    let payment: typeof invoicePayments[0] | undefined;
    let paymentIndex: number;
    if (paymentId !== null) {
      payment = payments.find((p: InvoicePaymentItem) => p.id === paymentId);
      paymentIndex = payments.findIndex((p: InvoicePaymentItem) => p.id === paymentId) + 1;
    } else {
      payment = payments[payments.length - 1];
      paymentIndex = payments.length;
    }
    if (!payment) {
      setSendReceiptPaymentId(paymentId);
      setSendReceiptOpen(true);
      return;
    }
    const receiptInvoice = getReceiptInvoiceData();
    const blobUrl = previewReceiptPDF(receiptInvoice, payment, paymentIndex, payments, companySettings ?? {});
    const fileName = receiptPDFFilename(receiptInvoice, paymentIndex);
    setReceiptPreviewPaymentId(paymentId);
    setReceiptPreviewBlobUrl(blobUrl);
    setReceiptPreviewFileName(fileName);
    setReceiptPreviewOpen(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-1/3" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-[400px] lg:col-span-2" />
          <Skeleton className="h-[400px]" />
        </div>
      </div>
    );
  }

  if (!invoice) return <div className="text-center py-12">Invoice not found</div>;

  // A container child (deposit/balance/milestone): its amounts are derived from
  // the parent, so its line items / discount / tax are read-only here.
  const isChildInvoice = invoice.isDepositChild || invoice.isBalanceChild || invoice.milestoneParentId != null;
  const childParentId = invoice.depositParentId ?? invoice.milestoneParentId ?? null;

  return (
    <div className="space-y-6 animate-in fade-in duration-300 pb-24">
      <div className="flex flex-col gap-4">
        <Link href="/invoices">
          <Button variant="ghost" size="sm" className="gap-1.5 -ml-2 text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> Back to Invoices
          </Button>
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-1.5 mb-1 group/num">
              <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Invoice #</span>
              <InlineEditField
                value={invoice.invoiceNumber || ""}
                onSave={(v) => saveInvoiceField({ invoiceNumber: v })}
                placeholder="e.g. INV-0001"
                emptyPlaceholder="Add number"
                displayClassName="text-xs font-semibold uppercase tracking-widest text-muted-foreground"
                inputClassName="h-6 text-xs px-1.5 py-0 w-28"
              />
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <InlineEditField
                value={invoice.title}
                onSave={(v) => saveInvoiceField({ title: v })}
                required
                requiredMessage="Invoice title is required."
                onValidationError={(msg) => toast({ title: "Validation error", description: msg, variant: "destructive" })}
                placeholder="Invoice title"
                displayClassName="text-3xl font-bold tracking-tight text-foreground"
                inputClassName="text-2xl font-bold h-10"
                className="flex-1"
              />
              <Badge variant="outline" className={cn("capitalize px-2.5 py-0.5 text-xs font-semibold", getStatusColor(invoice.status))}>
                {invoice.status}
              </Badge>
              {invoice.isRecurring && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-violet-50 text-violet-700 border border-violet-200 rounded-full font-medium">
                  <RefreshCw className="w-3 h-3" />
                  Recurring · {
                    RECURRING_INTERVAL_OPTIONS.find(o => o.value === normalizeInterval(invoice.recurringInterval))?.label
                    ?? invoice.recurringInterval ?? ""
                  }
                </span>
              )}
            </div>
            <p className="text-muted-foreground mt-1 flex items-center gap-2 text-sm flex-wrap">
              <InlineCustomerSelect
                customerName={invoice.customerName}
                customerId={invoice.customerId}
                customers={customers}
                open={inlineCustomerOpen}
                search={inlineCustomerSearch}
                onOpenChange={setInlineCustomerOpen}
                onSearchChange={setInlineCustomerSearch}
                onSelect={handleInlineCustomerChange}
                onCreateNew={(name) => {
                  setPendingCustomerName(name);
                  setCreateCustomerModalOpen(true);
                  setInlineCustomerOpen(false);
                }}
              />
              <span className="text-muted-foreground/50">•</span>
              <Link href={`/customers/${invoice.customerId}`} className="hover:text-primary hover:underline text-xs">
                View profile
              </Link>
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-1">
              <InlineEditDate
                value={invoice.invoiceDate || ""}
                onSave={(v) => saveInvoiceField({ invoiceDate: v })}
                label="Invoice date"
                formatDate={formatDate}
                emptyPlaceholder="Set date"
              />
              <span className="text-muted-foreground/50 text-xs">•</span>
              <InlineEditDate
                value={invoice.dueDate || ""}
                onSave={(v) => saveInvoiceField({ dueDate: v })}
                label="Due date"
                formatDate={formatDate}
                emptyPlaceholder={invoice.status === "overdue" ? "Overdue" : "Set date"}
                className={invoice.status === "overdue" && invoice.dueDate ? "text-red-600" : undefined}
              />
            </div>
            <div style={{ marginTop: "0.5rem" }}>
              <ProjectAssignment kind="invoice" id={invoiceId} />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2" data-tour-target="invoice-actions">
            {invoice.status === "overdue" && invoice.overdueReminderEnabled && (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    className="h-10 w-10 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                    variant="outline"
                    onClick={handleSendReminder}
                    disabled={sendReminderMutation.isPending}
                    aria-label="Send overdue reminder"
                  >
                    {sendReminderMutation.isPending ? (
                      <Loader2 className="w-5 h-5 animate-spin" strokeWidth={2.25} />
                    ) : (
                      <BellRing className="w-5 h-5" strokeWidth={2.25} />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Send overdue reminder email</TooltipContent>
              </Tooltip>
            )}
            {invoice.status === "paid" && invoice.stripePaymentStatus !== "paid" && (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    className="h-10 w-10 border-green-300 text-green-700 hover:bg-green-50 hover:text-green-800"
                    variant="outline"
                    onClick={() => openReceiptPreview(null)}
                    aria-label="Preview payment receipt"
                  >
                    <Receipt className="w-5 h-5" strokeWidth={2.25} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Preview and download payment receipt</TooltipContent>
              </Tooltip>
            )}
            <QuickBooksPushButton kind="invoice" id={invoiceId} />
                <XeroPushButton kind="invoice" id={invoiceId} />
                {invoice.isContainer ? (
                  <>
                    <Button size="sm" variant="outline" onClick={onDuplicate} disabled={duplicateInvoice.isPending}>
                      {duplicateInvoice.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Copy className="w-4 h-4 mr-2" />}
                      Duplicate
                    </Button>
                    <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => setIsDeleteOpen(true)}>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </>
                ) : (
                  <EditorActionBar
                    tourTargets={{ sendEmailButton: "invoice-send-btn" }}
                    statusOptions={[
                      { label: "Mark as Draft", value: "draft", description: "Not yet sent — hidden from paid/overdue metrics." },
                      { label: "Mark as Sent", value: "sent", description: "Marks it delivered to the customer and awaiting payment." },
                      { label: "Mark as Paid", value: "paid", description: "Records the invoice as fully paid and counts toward revenue." },
                      {
                        label: "Mark as Overdue",
                        value: "overdue",
                        disabled: !(invoice?.dueDate ? new Date(invoice.dueDate) < new Date(new Date().toDateString()) : false),
                        description: "Flags it as past its due date. Only available after the due date.",
                      },
                      { label: "Mark as Void", value: "void", description: "Cancels the invoice without deleting it — excluded from revenue, kept for your records." },
                    ]}
                    currentStatus={invoice.status}
                    onStatusChange={onStatusChange}
                    previewOptions={[
                      {
                        label: "Invoice PDF",
                        description: "Customer-facing invoice",
                        icon: <Receipt className="w-4 h-4 mr-2 text-blue-500" />,
                        onClick: () => handlePdfAction("preview"),
                      },
                    ]}
                    sendEmailFeature="EMAIL_SENDING"
                    onSendEmail={() => {
                      const applyVars = (template: string, vars: Record<string, string>) =>
                        template.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`);
                      const vars = {
                        customerName: invoice.customerName || "there",
                        quoteNumber: "",
                        invoiceNumber: invoice.invoiceNumber || "",
                        companyName: companySettings?.companyName || "us",
                      };
                      const defaultSubject = `Invoice ${invoice.invoiceNumber ? `#${invoice.invoiceNumber}` : ""} from ${companySettings?.companyName || "us"}`;
                      const defaultBody = `Hi ${invoice.customerName || "there"},\n\nPlease find your invoice attached. Let us know if you have any questions.\n\nThank you for your business!`;
                      setEmailSubject(companySettings?.defaultInvoiceEmailSubject ? applyVars(companySettings.defaultInvoiceEmailSubject, vars) : defaultSubject);
                      setEmailMessage(companySettings?.defaultInvoiceEmailBody ? applyVars(companySettings.defaultInvoiceEmailBody, vars) : defaultBody);
                      setSendEmailOpen(true);
                    }}
                    exportOptions={[
                      {
                        label: "Invoice PDF",
                        description: "Customer-facing invoice",
                        icon: <Receipt className="w-4 h-4 mr-2 text-blue-500" />,
                        onClick: () => handlePdfAction("export"),
                      },
                    ]}
                    onDuplicate={onDuplicate}
                    isDuplicating={duplicateInvoice.isPending}
                    duplicateLabel="Duplicate"
                    onDelete={() => setIsDeleteOpen(true)}
                    deleteLabel="Delete"
                    onSaveAsTemplate={() => {
                      setSaveAsTemplateName(invoice?.title || "");
                      setSaveAsTemplateDesc("");
                      setSaveAsTemplateOpen(true);
                    }}
                  />
                )}
          </div>
        </div>
      </div>

      {invoice.isContainer && (
        <div className="flex items-center gap-2 px-4 py-2 rounded-md bg-blue-50 border border-blue-200 text-blue-800 text-sm">
          <FileText className="w-4 h-4 text-blue-500 shrink-0" />
          <span>This invoice is a container — it aggregates its deposit/balance invoices and is never sent directly. Send those from the Payment Plan section below.</span>
        </div>
      )}

      <hr className="border-border" />

      {invoice.linkedEstimateId && (
        <div className="flex items-center gap-2 px-4 py-2 rounded-md bg-blue-50 border border-blue-200 text-blue-800 text-sm">
          <FileText className="w-4 h-4 text-blue-500 shrink-0" />
          <span>Generated from quote.</span>
          <Link href={`/quotes/${invoice.linkedEstimateId}`} className="underline font-medium hover:text-blue-900">
            View original quote →
          </Link>
        </div>
      )}

      {invoice.autoChargeFailedAt && (
        <AutoChargeFailedBanner failedAt={invoice.autoChargeFailedAt} />
      )}

      {isChildInvoice && childParentId != null && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm">
          <Receipt className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>
            {invoice.isDepositChild
              ? "This is a deposit invoice."
              : invoice.isBalanceChild
              ? "This is a remaining-balance invoice."
              : "This is a milestone invoice."}{" "}
            Its amounts are set by the parent invoice; payments here apply to the parent.{" "}
            <Link href={`/invoices/${childParentId}`} className="underline font-medium hover:text-emerald-950">
              View parent invoice →
            </Link>
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
        <div className="md:col-span-2 space-y-6">
          <Card data-walkthrough-target="invoice-line-items" className="shadow-sm border-border/50">
            <CardHeader className="flex flex-row items-center justify-between pb-2 border-b gap-3 bg-muted/40">
              <div className="shrink-0">
                <CardTitle className="text-lg">Line Items</CardTitle>
                <CardDescription>{isChildInvoice ? "Managed by the parent invoice" : "Billable services and products"}</CardDescription>
              </div>
              {!isChildInvoice && (
              <div className="flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 text-xs gap-1">
                      <Plus className="w-3.5 h-3.5" />
                      Add
                      <ChevronDown className="w-3 h-3 opacity-60" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuItem onClick={() => setAddLineItemOpen(true)}>
                      <Plus className="w-3.5 h-3.5 mr-2" />
                      Add Line Item
                    </DropdownMenuItem>
                    {isPro ? (
                      <DropdownMenuItem onClick={handleAddInvoiceGroup} disabled={createInvoiceGroup.isPending}>
                        {createInvoiceGroup.isPending ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-2" />}
                        Add Group
                      </DropdownMenuItem>
                    ) : (
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <DropdownMenuItem asChild>
                            <Link href="/upgrade" className="flex items-center w-full text-amber-700 dark:text-amber-400 focus:text-amber-700 dark:focus:text-amber-400">
                              <Plus className="w-3.5 h-3.5 mr-2 opacity-60" />
                              <span className="opacity-60">Add Group</span>
                              <Lock className="w-3.5 h-3.5 ml-auto" />
                            </Link>
                          </DropdownMenuItem>
                        </TooltipTrigger>
                        <TooltipContent>Upgrade to Pro to unlock {PRO_FEATURES.LINE_ITEM_GROUPS}</TooltipContent>
                      </Tooltip>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              )}
            </CardHeader>
            {addLineItemOpen && (
              <AddInvoiceItemSearch invoiceId={invoiceId} onClose={() => setAddLineItemOpen(false)} />
            )}
            {addingToGroupId !== null && (
              <AddInvoiceItemSearch invoiceId={invoiceId} groupId={addingToGroupId} onClose={() => setAddingToGroupId(null)} />
            )}
            <CardContent className="p-0">
              {isChildInvoice ? (
                <div className="border-b divide-y divide-gray-200">
                  {[...invoice.lineItems].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)).map((item) => (
                    <div key={item.id} className="flex items-start justify-between gap-3 px-4 py-3 text-sm">
                      <div className="min-w-0">
                        <div className="font-medium text-foreground truncate">{item.description || "—"}</div>
                        <div className="text-xs text-muted-foreground">{item.quantity} × {formatCurrency(item.unitPrice)}</div>
                      </div>
                      <div className="font-semibold shrink-0">{formatCurrency(item.quantity * item.unitPrice)}</div>
                    </div>
                  ))}
                  {invoice.lineItems.length === 0 && (
                    <div className="px-4 py-6 text-sm text-muted-foreground text-center">No line items.</div>
                  )}
                </div>
              ) : (
              <div className="border-b">
                  {(() => {
                    const sortedGroups = [...(invoice.groups ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
                    const sorted = [...invoice.lineItems].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
                    const ungroupedItems = sorted.filter(li => !li.groupId);
                    return (
                      <>
                        <div className="divide-y divide-gray-300">
                          {ungroupedItems.map((item) => (
                            <LineItemRow
                              key={item.id}
                              item={item}
                              invoiceId={invoiceId}
                              allItems={sorted}
                              index={sorted.findIndex(i => i.id === item.id)}
                              groups={sortedGroups}
                            />
                          ))}
                          <button
                            type="button"
                            onClick={() => setAddLineItemOpen(true)}
                            className="w-full flex items-center justify-center gap-2 py-3.5 text-sm text-muted-foreground hover:text-primary hover:bg-muted/30 border-t border-dashed border-border/50 transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                            Add line item
                          </button>
                        </div>
                        {sortedGroups.map((group) => {
                          const groupItems = sorted.filter(li => li.groupId === group.id);
                          return (
                            <InvoiceGroupHeader
                              key={group.id}
                              group={group}
                              invoiceId={invoiceId}
                              items={groupItems}
                              allGroups={sortedGroups}
                            >
                              <div className="divide-y divide-gray-300">
                                {groupItems.map((item) => (
                                  <LineItemRow
                                    key={item.id}
                                    item={item}
                                    invoiceId={invoiceId}
                                    allItems={sorted}
                                    index={sorted.findIndex(i => i.id === item.id)}
                                    groups={sortedGroups}
                                  />
                                ))}
                                <button
                                  type="button"
                                  onClick={() => setAddingToGroupId(group.id)}
                                  className="w-full flex items-center justify-center gap-2 py-2.5 text-sm text-muted-foreground hover:text-primary hover:bg-muted/30 border-dashed border-t border-border/50 transition-colors"
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                  Add line item
                                </button>
                              </div>
                            </InvoiceGroupHeader>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
              )}
              <PricingSummary invoice={invoice} invoiceId={invoiceId} formatCurrency={formatCurrency} companySettings={companySettings} readOnly={isChildInvoice} />
              {invoicePayments.length > 0 && !invoice.isMilestoneParent && (
                <div className="border-t px-4 py-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                      <CreditCard className="w-4 h-4 text-muted-foreground" /> Payment History
                    </h4>
                    {(invoice.status === "partial") && (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={openRecordPayment}>
                        <Plus className="w-3 h-3 mr-1" /> Record Another Payment
                      </Button>
                    )}
                  </div>
                  {(() => {
                    const totalPaid = invoicePayments.reduce((s: number, p: InvoicePaymentItem) => s + p.amount, 0);
                    const balance = Math.max(0, (invoice.total ?? 0) - totalPaid);
                    return (
                      <div className="space-y-2">
                        {invoicePayments.map((pmt: InvoicePaymentItem, pmtIndex: number) => (
                          <div key={pmt.id} className="flex items-center justify-between text-sm bg-muted/30 rounded-md px-3 py-2 gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-green-700">{formatCurrency(pmt.amount)}</span>
                                <span className="text-muted-foreground text-xs">{pmt.paymentDate}</span>
                                {pmt.paymentMethod && (
                                  <span className="text-xs bg-secondary text-secondary-foreground px-1.5 py-0.5 rounded">
                                    {pmt.paymentMethod === "cash" ? "Cash"
                                      : pmt.paymentMethod === "cheque" ? "Check"
                                      : pmt.paymentMethod === "bank_transfer" ? "Bank Transfer"
                                      : pmt.paymentMethod === "e-transfer" ? "E-Transfer"
                                      : pmt.paymentMethod === "credit_card" ? "Credit Card"
                                      : pmt.paymentMethod === "online" ? "Online"
                                      : pmt.paymentMethod === "other" ? "Other"
                                      : pmt.paymentMethod.charAt(0).toUpperCase() + pmt.paymentMethod.slice(1)}
                                  </span>
                                )}
                                {pmt.receiptSentAt && (
                                  <span className="text-xs text-green-600 flex items-center gap-0.5">
                                    <CheckCircle className="w-3 h-3" /> Receipt sent
                                  </span>
                                )}
                              </div>
                              {pmt.notes && <p className="text-xs text-muted-foreground mt-0.5 truncate">{pmt.notes}</p>}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-xs"
                                title="Preview and download receipt"
                                {...(pmtIndex === 0 ? { "data-tour-target": "invoice-receipt-btn" } : {})}
                                onClick={() => openReceiptPreview(pmt.id)}
                              >
                                <Eye className="w-3 h-3 mr-1" /> Preview Receipt
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-xs"
                                disabled={sendPaymentReceiptMutation.isPending || !!pmt.receiptSentAt}
                                title={pmt.receiptSentAt ? "Receipt already sent" : "Send payment receipt to customer"}
                                onClick={() => {
                                  setSendReceiptPaymentId(pmt.id);
                                  setSendReceiptOpen(true);
                                }}
                              >
                                <Receipt className="w-3 h-3 mr-1" /> {pmt.receiptSentAt ? "Receipt Sent" : "Send Receipt"}
                              </Button>
                              <Tooltip delayDuration={0}>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                    disabled={deletePaymentMutation.isPending}
                                    onClick={() => setDeletePaymentId(pmt.id)}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Delete payment</TooltipContent>
                              </Tooltip>
                            </div>
                          </div>
                        ))}
                        <div className="flex justify-between items-center text-sm pt-1 border-t">
                          <span className="text-muted-foreground">Total Paid</span>
                          <span className="font-semibold text-green-700">{formatCurrency(totalPaid)}</span>
                        </div>
                        {balance > 0 && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground inline-flex items-center gap-1">Balance Owing <InfoTip content={FORMULAS.amountDue} /></span>
                            <span className="font-semibold text-red-600">{formatCurrency(balance)}</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
              {invoicePayments.length === 0 && !invoice.isMilestoneParent && (invoice.status === "sent" || invoice.status === "overdue" || invoice.status === "partial") && (
                <div className="border-t px-4 py-3 flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">No payments recorded yet.</span>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={openRecordPayment} data-tour-target="invoice-record-payment-btn">
                    <CreditCard className="w-3 h-3 mr-1" /> Record Payment
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-sm border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Description</CardTitle>
            </CardHeader>
            <CardContent>
              <InlineEditField
                value={invoice.description || ""}
                onSave={(v) => saveInvoiceField({ description: v })}
                multiline
                placeholder="Add a description..."
                emptyPlaceholder="Click to add a description..."
                displayClassName="whitespace-pre-wrap text-sm"
                inputClassName="text-sm"
              />
            </CardContent>
          </Card>

          <Card className="shadow-sm border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Notes / Terms</CardTitle>
            </CardHeader>
            <CardContent>
              <InlineEditField
                value={invoice.termsAndNotes || ""}
                onSave={(v) => saveInvoiceField({ termsAndNotes: v || null })}
                multiline
                placeholder="Add notes or terms for this invoice..."
                emptyPlaceholder="Click to add notes or terms..."
                displayClassName="whitespace-pre-wrap text-sm"
                inputClassName="text-sm"
              />
            </CardContent>
          </Card>
        </div>

        <div className="md:col-span-1 space-y-4">
          <PaymentLinkPanel invoice={invoice} invoiceId={invoiceId} />
          <OverdueRemindersPanel invoice={invoice} invoiceId={invoiceId} />
          <div data-walkthrough-target="payment-plan-panel">
            <InvoicePaymentPlanSection
              invoice={invoice}
              invoiceId={invoiceId}
              formatCurrency={formatCurrency}
              invoiceMilestones={invoiceMilestones ?? []}
              isRecurringActive={!!(invoice.isRecurring)}
              onLocalModeChange={setLocalPaymentPlanMode}
            />
          </div>
          <ProGate feature={GATE_RECURRING} mode="overlay">
            <RecurringPanel
              invoice={invoice}
              invoiceId={invoiceId}
              isPaymentPlanActive={
                localPaymentPlanMode !== "none" ||
                (invoice.depositValue ?? 0) > 0 ||
                !!(invoice.isMilestoneParent) ||
                (invoiceMilestones?.length ?? 0) > 0
              }
            />
          </ProGate>
        </div>
      </div>

      <AlertDialog open={markSentForPdfOpen} onOpenChange={(open) => { if (!open) { setMarkSentForPdfOpen(false); setPendingPdfAction(null); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Include payment link in PDF?</AlertDialogTitle>
            <AlertDialogDescription>
              This invoice is still a draft. Mark it as sent to generate a payment link — your customer can then pay directly from the PDF.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <button
              type="button"
              onClick={handleExportAsDraft}
              className="inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-4 py-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              Export as Draft
            </button>
            <AlertDialogAction onClick={() => void handleMarkSentAndExport()} disabled={updateInvoice.isPending}>
              {updateInvoice.isPending ? "Marking as sent…" : "Mark as Sent & Export"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              {invoice.isContainer
                ? "This invoice and its deposit, balance and milestone invoices will be permanently deleted. This action cannot be undone."
                : "This invoice will be permanently deleted. This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={onDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PDFPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        blobUrl={previewBlobUrl}
        title={previewTitle}
      />

      <PDFPreviewDialog
        open={receiptPreviewOpen}
        onOpenChange={(open) => {
          setReceiptPreviewOpen(open);
          if (!open) {
            setReceiptPreviewBlobUrl(null);
            setReceiptPreviewPaymentId(null);
          }
        }}
        blobUrl={receiptPreviewBlobUrl}
        title="Payment Receipt"
        fileName={receiptPreviewFileName}
        onSendEmail={() => {
          setReceiptPreviewOpen(false);
          setSendReceiptPaymentId(receiptPreviewPaymentId);
          setSendReceiptOpen(true);
        }}
      />

      <Dialog open={sendEmailOpen} onOpenChange={(open) => { setSendEmailOpen(open); if (!open) setCcSelf(true); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-4 h-4" /> Send Invoice via Email
            </DialogTitle>
            <DialogDescription>
              {invoice?.customerEmail
                ? <>Sending to <span className="font-medium text-foreground">{invoice.customerEmail}</span></>
                : <span className="text-destructive">No email on file for this customer. Please add one to their record first.</span>
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium block mb-1">Subject</label>
              <Input value={emailSubject} onChange={e => setEmailSubject(e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Message</label>
              <Textarea
                value={emailMessage}
                onChange={e => setEmailMessage(e.target.value)}
                className="min-h-[120px] resize-none text-sm"
              />
            </div>
            {companySettings?.replyToEmail ? (
              <div className="flex items-center gap-2">
                <input
                  id="cc-self-checkbox"
                  type="checkbox"
                  checked={ccSelf}
                  onChange={e => setCcSelf(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 accent-primary cursor-pointer"
                />
                <label htmlFor="cc-self-checkbox" className="text-sm cursor-pointer select-none">
                  Send a copy to myself <span className="text-muted-foreground">({companySettings.replyToEmail})</span>
                </label>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                To CC yourself on sent invoices, add a reply-to email in{" "}
                <a href="/settings" className="underline hover:text-foreground transition-colors">Settings</a>.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" data-walkthrough-target="send-email-cancel-btn" onClick={() => { setSendEmailOpen(false); setCcSelf(true); }}>Cancel</Button>
            <Button
              data-walkthrough-target="send-email-submit-btn"
              disabled={!invoice?.customerEmail || sendEmailMutation.isPending || !emailSubject || !emailMessage}
              onClick={async () => {
                if (!invoice?.customerEmail) return;
                const attachmentBase64 = await invoicePDFBase64(getInvoiceForPdf(), companySettings ?? {});
                const attachmentFilename = invoicePDFFilename(invoice);
                sendEmailMutation.mutate(
                  { id: invoiceId, body: { subject: emailSubject, message: emailMessage, attachmentBase64, attachmentFilename, ccSelf } },
                  {
                    onSuccess: () => {
                      setSendEmailOpen(false);
                      setCcSelf(true);
                      queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
                      queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
                      toast({ title: "Email sent", description: `Invoice sent to ${invoice.customerEmail}` });
                      emitTourEvent("invoice.sent");
                    },
                    onError: (err: unknown) => {
                      const msg = err instanceof Error ? err.message : "Failed to send email";
                      toast({ title: "Failed to send", description: msg, variant: "destructive" });
                    }
                  }
                );
              }}
            >
              {sendEmailMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <><Mail className="w-4 h-4 mr-2" /> Send Email</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deletePaymentId} onOpenChange={(open) => { if (!open) setDeletePaymentId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Payment?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this payment record. The invoice status will be recalculated automatically.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletePaymentMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deletePaymentMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!deletePaymentId) return;
                deletePaymentMutation.mutate(
                  { invoiceId, paymentId: deletePaymentId },
                  {
                    onSuccess: (result) => {
                      setDeletePaymentId(null);
                      queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
                      queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
                      queryClient.invalidateQueries({ queryKey: getListInvoicePaymentsQueryKey(invoiceId) });
                      const statusMsg = result.totalPaid > 0
                        ? `Balance owing: ${formatCurrency(result.balanceOwing)}`
                        : "Invoice status updated.";
                      toast({ title: "Payment deleted", description: statusMsg });
                    },
                    onError: (err: unknown) => {
                      const e = err as { response?: { data?: { error?: string } }; message?: string };
                      const msg = e?.response?.data?.error || e?.message || "Failed to delete payment";
                      toast({ title: msg, variant: "destructive" });
                    },
                  }
                );
              }}
            >
              {deletePaymentMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting…</> : "Delete Payment"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={sendReceiptOpen} onOpenChange={(open) => { setSendReceiptOpen(open); if (!open) { setSendReceiptPaymentId(null); setCcSelfReceipt(false); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Receipt className="w-4 h-4" /> Send Payment Receipt
            </AlertDialogTitle>
            <AlertDialogDescription>
              {invoice?.customerEmail ? (
                <>
                  A payment receipt{sendReceiptPaymentId ? " for this payment" : ""} confirming invoice {invoice.invoiceNumber ? `#${invoice.invoiceNumber}` : ""} will be sent to{" "}
                  <span className="font-medium text-foreground">{invoice.customerEmail}</span>.
                </>
              ) : (
                <span className="text-destructive">No email on file for this customer. Please add one to their record first.</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {user?.email && (
            <div className="flex items-center gap-2 px-1">
              <input
                id="receipt-cc-self-checkbox"
                type="checkbox"
                checked={ccSelfReceipt}
                onChange={e => setCcSelfReceipt(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 accent-primary cursor-pointer"
              />
              <label htmlFor="receipt-cc-self-checkbox" className="text-sm cursor-pointer select-none">
                Send me a copy <span className="text-muted-foreground">({user.email})</span>
              </label>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!invoice?.customerEmail || sendReceiptMutation.isPending || sendPaymentReceiptMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!invoice?.customerEmail) return;
                if (sendReceiptPaymentId) {
                  sendPaymentReceiptMutation.mutate({ invoiceId, paymentId: sendReceiptPaymentId, ccSelf: ccSelfReceipt || undefined }, {
                    onSuccess: (result) => {
                      setSendReceiptOpen(false);
                      setSendReceiptPaymentId(null);
                      setCcSelfReceipt(false);
                      queryClient.invalidateQueries({ queryKey: getListInvoicePaymentsQueryKey(invoiceId) });
                      toast({ title: "Receipt sent", description: `Receipt emailed to ${result.to}` });
                    },
                    onError: (err: unknown) => {
                      const e = err as { response?: { data?: { error?: string } }; message?: string };
                      const msg = e?.response?.data?.error || e?.message || "Failed to send receipt";
                      toast({ title: msg, variant: "destructive" });
                    },
                  });
                } else {
                  sendReceiptMutation.mutate({ id: invoiceId, ccSelf: ccSelfReceipt || undefined }, {
                    onSuccess: (result) => {
                      setSendReceiptOpen(false);
                      setCcSelfReceipt(false);
                      toast({ title: "Receipt sent", description: `Receipt emailed to ${result.to}` });
                    },
                    onError: (err: unknown) => {
                      const e = err as { response?: { data?: { error?: string } }; message?: string };
                      const msg = e?.response?.data?.error || e?.message || "Failed to send receipt";
                      toast({ title: msg, variant: "destructive" });
                    },
                  });
                }
              }}
            >
              {(sendReceiptMutation.isPending || sendPaymentReceiptMutation.isPending) ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
              ) : (
                "Send Receipt"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={recordPaymentOpen} onOpenChange={(open) => { if (!recordPaymentMutation.isPending) setRecordPaymentOpen(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" /> Record Payment
            </DialogTitle>
            <DialogDescription>
              {(() => {
                const total = invoice?.total ?? 0;
                const alreadyPaid = invoicePayments.reduce((s: number, p: InvoicePaymentItem) => s + p.amount, 0);
                const balance = Math.max(0, total - alreadyPaid);
                return (
                  <span>
                    Invoice total: <strong>{formatCurrency(total)}</strong>
                    {alreadyPaid > 0 && <> · Already paid: <strong>{formatCurrency(alreadyPaid)}</strong> · Balance: <strong>{formatCurrency(balance)}</strong></>}
                  </span>
                );
              })()}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium block mb-1">Amount Received <span className="text-destructive">*</span></label>
              <div className="relative">
                <DollarSign className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={paymentAmount}
                  onChange={e => setPaymentAmount(e.target.value)}
                  className="pl-8"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Payment Method</label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger>
                  <SelectValue placeholder="Select method (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Check</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="e-transfer">E-Transfer</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Payment Date</label>
              <Input
                type="date"
                value={paymentDate}
                onChange={e => setPaymentDate(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Notes <span className="text-muted-foreground font-normal">(optional)</span></label>
              <Input
                value={paymentNotes}
                onChange={e => setPaymentNotes(e.target.value)}
                placeholder="e.g. Paid via bank transfer, check #1234…"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={recordPaymentMutation.isPending} onClick={() => setRecordPaymentOpen(false)}>Cancel</Button>
            <Button
              disabled={!paymentAmount || parseFloat(paymentAmount) <= 0 || recordPaymentMutation.isPending}
              onClick={() => {
                recordPaymentMutation.mutate(
                  { invoiceId, data: { amount: parseFloat(paymentAmount), paymentDate, notes: paymentNotes || undefined, paymentMethod: paymentMethod || undefined } },
                  {
                    onSuccess: (result) => {
                      setRecordPaymentOpen(false);
                      queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
                      queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
                      queryClient.invalidateQueries({ queryKey: getListInvoicePaymentsQueryKey(invoiceId) });
                      const statusMsg = result.invoiceStatus === "paid" ? "Invoice marked as Paid." : `Balance owing: ${formatCurrency(result.balanceOwing)}`;
                      toast({ title: `Payment of ${formatCurrency(result.payment.amount)} recorded`, description: statusMsg });
                      emitTourEvent("invoice.payment-recorded");
                    },
                    onError: (err: unknown) => {
                      const e = err as { response?: { data?: { error?: string } }; message?: string };
                      const msg = e?.response?.data?.error || e?.message || "Failed to record payment";
                      toast({ title: msg, variant: "destructive" });
                    },
                  }
                );
              }}
            >
              {recordPaymentMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Recording…</> : <><CreditCard className="w-4 h-4 mr-2" /> Record Payment</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={saveAsTemplateOpen} onOpenChange={(o) => { if (!isSavingTemplate) setSaveAsTemplateOpen(o); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Save as Template
            </DialogTitle>
            <DialogDescription>
              Save this invoice as a reusable template. Line items, costs, tax, deposit, and recurring settings will be captured.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium block mb-1">Template Name</label>
              <Input
                value={saveAsTemplateName}
                onChange={e => setSaveAsTemplateName(e.target.value)}
                placeholder="e.g. Monthly Maintenance Invoice"
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Description <span className="text-muted-foreground font-normal">(optional)</span></label>
              <Input
                value={saveAsTemplateDesc}
                onChange={e => setSaveAsTemplateDesc(e.target.value)}
                placeholder="Brief description of when to use this template"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={isSavingTemplate} onClick={() => setSaveAsTemplateOpen(false)}>Cancel</Button>
            <Button
              disabled={!saveAsTemplateName.trim() || isSavingTemplate}
              onClick={async () => {
                setIsSavingTemplate(true);
                try {
                  const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
                  const res = await fetch(`${BASE}/api/invoices/${invoiceId}/save-as-template`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({ name: saveAsTemplateName.trim(), description: saveAsTemplateDesc.trim() || null }),
                  });
                  if (!res.ok) throw new Error("Failed");
                  setSaveAsTemplateOpen(false);
                  toast({
                    title: "Template saved",
                    description: (
                      <span>
                        "{saveAsTemplateName.trim()}" saved.{" "}
                        <a href="/templates" className="underline font-medium">View Templates →</a>
                      </span>
                    ),
                  });
                } catch {
                  toast({ title: "Failed to save template", variant: "destructive" });
                } finally {
                  setIsSavingTemplate(false);
                }
              }}
            >
              {isSavingTemplate ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : "Save Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateCustomerModal
        open={createCustomerModalOpen}
        initialName={pendingCustomerName}
        isSubmitting={createCustomer.isPending}
        onOpenChange={setCreateCustomerModalOpen}
        onSubmit={handleCreateCustomerFromInline}
      />

      <EditorDisclaimer />
    </div>
  );
}
